import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import { BengkungRankInfo, BELT_COLOR_PRESETS, BeltColorPreset } from '../../data/bengkungRanks';
import { BengkungCulaBadge } from '../../components/common/BengkungCulaBadge';
import { Modal } from '../../components/common/Modal';
import { isSuperAdmin } from '../../utils/formatters';
import { processUploadedQrToSvg } from '../../utils/qrProcessor';
import {
  ALERT_EVENT_TYPES,
  ALERT_TEMPLATE_VARIABLES,
  DEFAULT_EVENT_TEMPLATES,
  AlertEventKey,
  normalizeEventTemplates,
  renderAlertTemplate,
  formatRelativeDay,
  formatShortDate
} from '../../utils/alertTemplate';
import {
  getUjianBengkungFeesDB,
  saveUjianBengkungFeesDB,
  UjianBengkungFeeConfig,
  DEFAULT_UJIAN_BENGKUNG_FEES,
  getUjianBengkungFeeForLevel,
  getBengkungRanksDB,
  saveBengkungRanksDB,
  resetBengkungRanksDB,
  getAlertSettingsDB,
  saveAlertSettingsDB,
  DEFAULT_ALERT_SETTINGS,
  getPersatuanPaymentSettingsDB,
  savePersatuanPaymentSettingsDB,
  DEFAULT_PERSATUAN_PAYMENT_SETTINGS
} from '../../services/db';
import { uploadToCloudinary } from '../../lib/cloudinary';
import { AlertNotificationSettings, PersatuanPaymentSettings, Gelanggang } from '../../types';
import {
  Settings,
  ShieldCheck,
  Save,
  RotateCcw,
  Award,
  AlertCircle,
  Check,
  Layers,
  Sliders,
  DollarSign,
  Info,
  Calendar,
  ChevronRight,
  ShieldAlert,
  ListOrdered,
  ArrowUp,
  ArrowDown,
  Tag,
  CheckCircle2,
  Plus,
  Pencil,
  Trash2,
  Bell,
  BellRing,
  Smartphone,
  Send,
  Clock,
  Server,
  Terminal,
  Copy,
  Zap,
  Radio,
  Sparkles,
  ToggleLeft,
  ToggleRight,
  X,
  Volume2,
  CreditCard,
  QrCode,
  Landmark,
  Building,
  Building2,
  ExternalLink,
  Wallet,
  Upload,
  Loader2,
  Download
} from 'lucide-react';

interface OrderNumberInputProps {
  currentPosition: number;
  maxPosition: number;
  onCommitPosition: (newPosition: number) => void;
}

const OrderNumberInput: React.FC<OrderNumberInputProps> = ({
  currentPosition,
  maxPosition,
  onCommitPosition
}) => {
  const [val, setVal] = useState<string>(String(currentPosition));
  const [isEditing, setIsEditing] = useState<boolean>(false);

  useEffect(() => {
    setVal(String(currentPosition));
  }, [currentPosition]);

  const handleCommit = () => {
    setIsEditing(false);
    const num = parseInt(val, 10);
    if (!isNaN(num) && num >= 1 && num <= maxPosition && num !== currentPosition) {
      onCommitPosition(num);
    } else {
      setVal(String(currentPosition));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setVal(String(currentPosition));
      setIsEditing(false);
    }
  };

  return (
    <input
      type="number"
      min={1}
      max={maxPosition}
      value={val}
      onFocus={(e) => {
        setIsEditing(true);
        e.target.select();
      }}
      onChange={(e) => setVal(e.target.value)}
      onBlur={handleCommit}
      onKeyDown={handleKeyDown}
      className={`w-9 h-8 rounded-xl text-center text-xs font-black shrink-0 shadow-2xs transition-all focus:outline-none border cursor-text [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${isEditing
        ? 'bg-white text-slate-900 border-asmm-red ring-2 ring-red-100 shadow-sm'
        : 'bg-slate-900 text-white border-slate-800 hover:bg-slate-800'
        }`}
      title="Klik untuk ubah nombor kedudukan terus"
    />
  );
};

const getInitialSettingsTab = (): 'alert' | 'payment' | 'bengkung' | 'yuran' => {
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname.toLowerCase();
    const hash = window.location.hash.toLowerCase();
    if (pathname.includes('/settings/bengkung') || pathname.includes('/settings/hierarchy') || hash.includes('bengkung')) {
      return 'bengkung';
    }
    if (pathname.includes('/settings/yuran') || pathname.includes('/settings/fees') || hash.includes('yuran') || hash.includes('fees')) {
      return 'yuran';
    }
    if (pathname.includes('/settings/payment') || pathname.includes('/settings/bayaran') || hash.includes('payment')) {
      return 'payment';
    }
    if (pathname.includes('/settings/alert') || pathname.includes('/settings/notifikasi') || pathname.includes('/settings/notification') || hash.includes('alert')) {
      return 'alert';
    }
  }
  return 'alert';
};

export const SettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const isUserSuperAdmin = isSuperAdmin(currentUser);

  // Active Main Tab in Settings: 'alert' (/settings/alert), 'payment' (/settings/payment), 'bengkung' (/settings/bengkung) vs 'yuran' (/settings/yuran)
  const [activeMainTab, setActiveMainTab] = useState<'alert' | 'payment' | 'bengkung' | 'yuran'>(getInitialSettingsTab);

  const handleTabSwitch = (tab: 'alert' | 'payment' | 'bengkung' | 'yuran') => {
    setActiveMainTab(tab);
    const targetPath = `/settings/${tab}`;
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
  };

  useEffect(() => {
    const syncFromUrl = () => {
      const tabFromUrl = getInitialSettingsTab();
      setActiveMainTab(tabFromUrl);
    };

    // Ensure initial URL specifies the sub-tab if just /settings or /settings/
    const pathname = window.location.pathname.toLowerCase();
    if (pathname === '/settings' || pathname === '/settings/') {
      window.history.replaceState(null, '', `/settings/${activeMainTab}`);
    }

    window.addEventListener('popstate', syncFromUrl);
    return () => window.removeEventListener('popstate', syncFromUrl);
  }, [activeMainTab]);

  // BENGKUNG LIST & ORDER STATE
  const [bengkungList, setBengkungList] = useState<BengkungRankInfo[]>([]);
  const [isSavingBengkung, setIsSavingBengkung] = useState<boolean>(false);
  const [bengkungFilterModule, setBengkungFilterModule] = useState<string>('all');

  // ADD BENGKUNG MODAL STATE
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [newCode, setNewCode] = useState<string>('');
  const [newRankTitle, setNewRankTitle] = useState<string>('');
  const [newBengkungName, setNewBengkungName] = useState<string>('');
  const [newModule, setNewModule] = useState<string>('Modul Bengkung Kuning');
  const [newCulaCount, setNewCulaCount] = useState<number>(0);
  const [newPresetId, setNewPresetId] = useState<string>('kuning');
  const [newInsertPos, setNewInsertPos] = useState<'end' | 'start' | 'after'>('end');
  const [newAfterCode, setNewAfterCode] = useState<string>('');
  const [isSubmittingAdd, setIsSubmittingAdd] = useState<boolean>(false);

  // EDIT BENGKUNG MODAL STATE
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [rankToEdit, setRankToEdit] = useState<BengkungRankInfo | null>(null);
  const [editCode, setEditCode] = useState<string>('');
  const [editRankTitle, setEditRankTitle] = useState<string>('');
  const [editBengkungName, setEditBengkungName] = useState<string>('');
  const [editModule, setEditModule] = useState<string>('Modul Bengkung Kuning');
  const [editCulaCount, setEditCulaCount] = useState<number>(0);
  const [editPresetId, setEditPresetId] = useState<string>('kuning');
  const [isSubmittingEdit, setIsSubmittingEdit] = useState<boolean>(false);

  // DELETE BENGKUNG MODAL STATE
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [rankToDelete, setRankToDelete] = useState<BengkungRankInfo | null>(null);
  const [isSubmittingDelete, setIsSubmittingDelete] = useState<boolean>(false);

  // ALERT SETTINGS STATE
  const [alertSettings, setAlertSettings] = useState<AlertNotificationSettings>(DEFAULT_ALERT_SETTINGS);
  const [isSavingAlert, setIsSavingAlert] = useState<boolean>(false);
  const [isTestingPush, setIsTestingPush] = useState<boolean>(false);
  const [copiedVapid, setCopiedVapid] = useState<boolean>(false);
  const [activeTplType, setActiveTplType] = useState<AlertEventKey>('latihan');
  const [previewOffsetDays, setPreviewOffsetDays] = useState<number>(1);
  const bodyTplRef = useRef<HTMLTextAreaElement>(null);

  const currentTemplates = useMemo(
    () => normalizeEventTemplates(alertSettings.eventTemplates, { title: alertSettings.titleTemplate, body: alertSettings.bodyTemplate }),
    [alertSettings.eventTemplates, alertSettings.titleTemplate, alertSettings.bodyTemplate]
  );
  const activeTpl = currentTemplates[activeTplType];

  const updateActiveTpl = (field: 'title' | 'body', value: string) => {
    setAlertSettings(prev => {
      const tpls = normalizeEventTemplates(prev.eventTemplates, { title: prev.titleTemplate, body: prev.bodyTemplate });
      tpls[activeTplType] = { ...tpls[activeTplType], [field]: value };
      return { ...prev, eventTemplates: tpls, titleTemplate: tpls.latihan.title, bodyTemplate: tpls.latihan.body };
    });
  };

  const insertTplVariable = (variable: string) => {
    const el = bodyTplRef.current;
    const current = activeTpl.body;
    if (!el) {
      updateActiveTpl('body', `${current} ${variable}`);
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + variable + current.slice(end);
    updateActiveTpl('body', next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + variable.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const previewVars = useMemo(() => {
    const samples: Record<AlertEventKey, { tajuk: string; lokasi: string; hour: number; minute: number; waktu: string }> = {
      latihan: { tajuk: 'Sesi Latihan Mingguan', lokasi: 'Gelanggang Pusaka Warisan', hour: 21, minute: 0, waktu: '9:00 PM' },
      acara: { tajuk: 'Majlis Hari Keluarga ASMM', lokasi: 'Dewan Serbaguna Kulim', hour: 8, minute: 30, waktu: '8:30 AM' },
      ujian: { tajuk: 'Ujian Kenaikan Bengkung', lokasi: 'Dewan Latihan ASMM', hour: 8, minute: 30, waktu: '8:30 AM' },
      mesyuarat: { tajuk: 'Mesyuarat Agung Tahunan', lokasi: 'Bilik Gerakan PSSASMM', hour: 21, minute: 0, waktu: '9:00 PM' },
    };
    const s = samples[activeTplType];
    const eventDate = new Date();
    eventDate.setDate(eventDate.getDate() + previewOffsetDays);
    eventDate.setHours(s.hour, s.minute, 0, 0);
    return {
      eventDate,
      waktu: s.waktu,
      gelanggang: 'Pusaka Warisan',
      tajuk: s.tajuk,
      lokasi: s.lokasi,
      namaAhli: currentUser?.name || 'Pesilat ASMM'
    };
  }, [activeTplType, previewOffsetDays, currentUser?.name]);

  const previewTitle = renderAlertTemplate(activeTpl.title, previewVars);
  const previewBody = renderAlertTemplate(activeTpl.body, previewVars);

  // PERSATUAN PAYMENT SETTINGS STATE
  const [paymentSettings, setPaymentSettings] = useState<PersatuanPaymentSettings>(DEFAULT_PERSATUAN_PAYMENT_SETTINGS);
  const [isSavingPayment, setIsSavingPayment] = useState<boolean>(false);
  const [isProcessingQrSvg, setIsProcessingQrSvg] = useState<boolean>(false);
  const [qrSvgString, setQrSvgString] = useState<string>('');

  // FEE CONFIG STATE
  const [feeConfig, setFeeConfig] = useState<UjianBengkungFeeConfig>(DEFAULT_UJIAN_BENGKUNG_FEES);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      getUjianBengkungFeesDB(),
      getBengkungRanksDB(),
      getAlertSettingsDB(),
      getPersatuanPaymentSettingsDB()
    ])
      .then(([cfg, ranks, alertCfg, payCfg]) => {
        setFeeConfig(cfg);
        setBengkungList(ranks);
        setAlertSettings(alertCfg);
        setPaymentSettings(payCfg);
        setIsLoading(false);
      })
      .catch(() => {
        setFeeConfig(DEFAULT_UJIAN_BENGKUNG_FEES);
        setAlertSettings(DEFAULT_ALERT_SETTINGS);
        setPaymentSettings(DEFAULT_PERSATUAN_PAYMENT_SETTINGS);
        setIsLoading(false);
      });
  }, []);

  // --- PERSATUAN PAYMENT SETTINGS HANDLERS ---
  const handleSavePaymentSettings = async () => {
    if (!isUserSuperAdmin) {
      showToast('error', 'Akses Ditolak', 'Hanya Superadmin dibenarkan menyimpan perubahan tetapan pembayaran.');
      return;
    }
    setIsSavingPayment(true);
    try {
      await savePersatuanPaymentSettingsDB(paymentSettings, currentUser?.name || 'Superadmin');
      showToast(
        'success',
        'Tetapan Pembayaran Persatuan Disimpan!',
        'Akaun bank, DuitNow QR, gateway ToyyibPay & peraturan Infaq/Vendor telah dikemaskini.'
      );
    } catch {
      showToast('error', 'Ralat', 'Gagal menyimpan tetapan pembayaran persatuan ke aaPanel.');
    } finally {
      setIsSavingPayment(false);
    }
  };

  const handleResetPaymentSettings = () => {
    setPaymentSettings(DEFAULT_PERSATUAN_PAYMENT_SETTINGS);
    showToast('info', 'Tetapan Dipulihkan', 'Tetapan akaun persatuan telah dipulihkan kepada nilai piawai.');
  };

  // Synchronize and digitalize QR image to 1:1 SVG
  useEffect(() => {
    const rawQr = paymentSettings.qrImageUrl || '';
    if (!rawQr) {
      setQrSvgString('');
      return;
    }

    const isAlreadySvg = rawQr.startsWith('data:image/svg+xml') || rawQr.trim().startsWith('<svg');
    if (isAlreadySvg) {
      if (rawQr.trim().startsWith('<svg')) {
        setQrSvgString(rawQr);
      } else if (rawQr.startsWith('data:image/svg+xml')) {
        try {
          const decoded = decodeURIComponent(rawQr.replace('data:image/svg+xml;charset=utf-8,', '').replace('data:image/svg+xml,', ''));
          setQrSvgString(decoded);
        } catch { }
      }
      return;
    }

    // Raster image detected: attempt automatic decoding into 1:1 SVG
    let isMounted = true;
    setIsProcessingQrSvg(true);
    processUploadedQrToSvg(rawQr, { colorDark: '#ED0080' })
      .then((res) => {
        if (!isMounted || !res) return;
        setQrSvgString(res.svgString);
        setPaymentSettings(prev => ({
          ...prev,
          qrImageUrl: res.svgDataUrl
        }));
      })
      .catch((err) => {
        console.warn('QR SVG processing notice:', err);
      })
      .finally(() => {
        if (isMounted) setIsProcessingQrSvg(false);
      });

    return () => { isMounted = false; };
  }, [paymentSettings.qrImageUrl]);

  const handleQrFileUpload = async (file: File) => {
    setIsProcessingQrSvg(true);
    try {
      const processed = await processUploadedQrToSvg(file, { colorDark: '#ED0080' });
      if (processed && processed.svgDataUrl) {
        setQrSvgString(processed.svgString);
        setPaymentSettings(prev => ({
          ...prev,
          qrImageUrl: processed.svgDataUrl
        }));
        showToast('success', 'Kod QR Didigitalkan ke 1:1 SVG!', 'Kod QR telah diekstrak dan dijana semula ke vektor SVG berkualiti tinggi.');
      } else {
        try {
          const cldRes = await uploadToCloudinary(file, {
            folder: 'asmm/qr',
            publicId: `qr-asmm-${Date.now()}`,
            resourceType: 'image'
          });
          setPaymentSettings(prev => ({ ...prev, qrImageUrl: cldRes.secure_url }));
          showToast('success', 'Imej QR Disimpan ke Cloudinary', 'Imej kod QR berjaya disimpan ke storan awan Cloudinary.');
        } catch (uploadErr) {
          console.warn('Fallback to local DataURL for QR:', uploadErr);
          const reader = new FileReader();
          reader.onload = (e) => {
            const res = e.target?.result as string;
            if (res) {
              setPaymentSettings(prev => ({ ...prev, qrImageUrl: res }));
              showToast('info', 'Imej QR Dimuat Naik', 'Imej dimuatkan secara lokal.');
            }
          };
          reader.readAsDataURL(file);
        }
      }
    } catch {
      showToast('error', 'Ralat Memproses QR', 'Gagal memproses fail imej kod QR.');
    } finally {
      setIsProcessingQrSvg(false);
    }
  };

  const handleDigitizeExistingUrl = async (sourceUrl: string) => {
    if (!sourceUrl) return;
    setIsProcessingQrSvg(true);
    try {
      const processed = await processUploadedQrToSvg(sourceUrl, { colorDark: '#ED0080' });
      if (processed && processed.svgDataUrl) {
        setQrSvgString(processed.svgString);
        setPaymentSettings(prev => ({
          ...prev,
          qrImageUrl: processed.svgDataUrl
        }));
        showToast('success', 'Kod QR Berjaya Didigitalkan!', 'Kod QR raster telah ditukar kepada digital SVG 1:1.');
      } else {
        showToast('warning', 'Tidak Dapat Mengesan QR', 'Sila muat naik fail imej kod QR yang lebih jelas.');
      }
    } catch {
      showToast('error', 'Ralat', 'Gagal mendigitalkan imej kod QR.');
    } finally {
      setIsProcessingQrSvg(false);
    }
  };

  const handleDownloadPersatuanQr = () => {
    if (!qrSvgString && !paymentSettings.qrImageUrl) return;
    const a = document.createElement('a');
    a.href = paymentSettings.qrImageUrl || `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrSvgString)}`;
    a.download = `Kod_QR_DuitNow_PSSASMM_Persatuan.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    showToast('success', 'Kod QR Dimuat Turun', 'Fail vektor SVG kod QR telah dimuat turun.');
  };


  // --- ALERT NOTIFICATION SETTINGS HANDLERS ---
  const handleSaveAlertSettings = async () => {
    if (!isUserSuperAdmin) {
      showToast('error', 'Akses Ditolak', 'Hanya Superadmin dibenarkan menyimpan perubahan tetapan alert.');
      return;
    }
    setIsSavingAlert(true);
    try {
      await saveAlertSettingsDB(alertSettings, currentUser?.name || 'Superadmin');
      showToast(
        'success',
        'Tetapan Alert & PWA Berjaya Disimpan!',
        'Konfigurasi notifikasi automatik sesi gelanggang dan Web Push telah disimpan ke pangkalan data aaPanel.'
      );
    } catch {
      showToast('error', 'Ralat', 'Gagal menyimpan tetapan alert ke pangkalan data aaPanel.');
    } finally {
      setIsSavingAlert(false);
    }
  };

  const handleResetAlertSettings = () => {
    setAlertSettings(DEFAULT_ALERT_SETTINGS);
    showToast('info', 'Tetapan Ditetapkan Semula', 'Tetapan amaran & PWA telah dipulihkan kepada nilai standard 20 jam ASMM.');
  };

  const handleCopyVapidKey = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(alertSettings.vapidPublicKey);
      setCopiedVapid(true);
      showToast('success', 'Kunci VAPID Disalin', 'Kunci VAPID Public Key telah disalin ke papan klip.');
      setTimeout(() => setCopiedVapid(false), 2000);
    }
  };

  const handleTestPushNotification = async () => {
    setIsTestingPush(true);
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        showToast('warning', 'Pelayar Tidak Menyokong Web Push', 'Pelayar atau mod peranti semasa tidak menyokong Web Notification API standard.');
        return;
      }

      let permission = Notification.permission;
      if (permission !== 'granted') {
        permission = await Notification.requestPermission();
      }

      const sampleTitle = previewTitle;
      const sampleBody = previewBody;

      if (permission === 'granted') {
        const actions = alertSettings.enableActionButtons ? [
          {
            action: 'confirm',
            title: alertSettings.actionConfirmLabel || 'Hadir?'
          },
          {
            action: 'decline',
            title: alertSettings.actionDeclineLabel || 'Cuti / Uzur ?'
          }
        ] : [];

        let shownViaSw = false;

        if ('serviceWorker' in navigator) {
          try {
            // Get active registration or register /sw.js
            let reg = await navigator.serviceWorker.getRegistration();
            if (!reg) {
              reg = await navigator.serviceWorker.register('/sw.js');
            }
            if (reg) {
              // Ensure sw is active/ready
              await navigator.serviceWorker.ready;
              await reg.showNotification(sampleTitle, {
                body: sampleBody,
                icon: '/logo-pssasmm.png',
                badge: '/logo-pssasmm.png',
                tag: 'asmm-gelanggang-rsvp-preview',
                renotify: true,
                requireInteraction: true,
                vibrate: alertSettings.enableVibration ? [200, 100, 200] : undefined,
                data: {
                  url: '/attendance',
                  gelanggang: 'Pusaka Warisan'
                },
                actions: actions
              } as any);
              shownViaSw = true;
            }
          } catch (swErr) {
            console.warn('SW notification fallback to window Notification:', swErr);
          }
        }

        if (!shownViaSw && typeof Notification !== 'undefined') {
          const notif = new Notification(sampleTitle, {
            body: sampleBody,
            icon: '/logo-pssasmm.png',
            badge: '/logo-pssasmm.png',
            tag: 'asmm-gelanggang-rsvp-preview'
          } as any);
          notif.onclick = () => {
            window.focus();
            notif.close();
          };
        }

        showToast(
          'success',
          'Notifikasi Toast Berjaya Dihantar!',
          `Pilihan jawapan [${alertSettings.actionConfirmLabel}] & [${alertSettings.actionDeclineLabel}] kini aktif pada notifikasi Android anda.`
        );
      } else {
        showToast(
          'warning',
          'Izin Notifikasi Belum Dibenarkan',
          'Sila tekan "Allow" pada pop-up kebenaran notifikasi pelayar untuk mengaktifkan Web Push.'
        );
      }
    } catch (err) {
      showToast('error', 'Ralat Ujian', 'Tidak dapat memaparkan notifikasi pada peranti.');
    } finally {
      setIsTestingPush(false);
    }
  };

  // --- BENGKUNG LIST REORDER & RENAME HANDLERS ---
  const handleMoveBengkung = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= bengkungList.length) return;
    setBengkungList(prev => {
      const copy = [...prev];
      const temp = copy[index];
      copy[index] = copy[targetIndex];
      copy[targetIndex] = temp;
      return copy;
    });
  };

  const handleMoveToPosition = (fromIndex: number, targetPosition: number) => {
    const toIndex = targetPosition - 1;
    if (toIndex < 0 || toIndex >= bengkungList.length || fromIndex === toIndex) return;
    setBengkungList(prev => {
      const copy = [...prev];
      const [movedItem] = copy.splice(fromIndex, 1);
      copy.splice(toIndex, 0, movedItem);
      return copy;
    });
  };

  const handleUpdateBengkungField = (index: number, field: 'bengkungName' | 'rankTitle', val: string) => {
    setBengkungList(prev => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        [field]: val
      };
      return copy;
    });
  };

  const handleSaveBengkungList = async () => {
    if (!isUserSuperAdmin) {
      showToast('error', 'Akses Ditolak', 'Hanya Superadmin dibenarkan menyimpan perubahan.');
      return;
    }
    setIsSavingBengkung(true);
    try {
      await saveBengkungRanksDB(bengkungList, currentUser?.name || 'Superadmin');
      showToast(
        'success',
        'Senarai Bengkung Berjaya Disimpan!',
        'Nama rasmi dan susunan keutamaan bengkung telah dikemaskini dalam seluruh sistem ASMM.'
      );
    } catch {
      showToast('error', 'Ralat', 'Gagal menyimpan senarai bengkung.');
    } finally {
      setIsSavingBengkung(false);
    }
  };

  const handleResetBengkungList = async () => {
    if (!isUserSuperAdmin) return;
    const defaults = await resetBengkungRanksDB(currentUser?.name || 'Superadmin');
    setBengkungList(defaults);
    showToast(
      'info',
      'Senarai Bengkung Dipulihkan',
      'Nama dan susunan bengkung telah ditetapkan semula kepada piawaian rasmi ASMM.'
    );
  };

  // --- ADD & DELETE BENGKUNG HANDLERS ---
  const handleOpenAddModal = () => {
    setNewCode('');
    setNewRankTitle('');
    setNewBengkungName('');
    setNewModule('Modul Bengkung Kuning');
    setNewCulaCount(0);
    setNewPresetId('kuning');
    setNewInsertPos('end');
    setNewAfterCode(bengkungList[bengkungList.length - 1]?.code || '');
    setIsAddModalOpen(true);
  };

  const handleModuleSelect = (mod: string) => {
    setNewModule(mod);
    const lower = mod.toLowerCase();
    if (lower.includes('hitam')) setNewPresetId('hitam');
    else if (lower.includes('putih')) setNewPresetId('putih');
    else if (lower.includes('merah')) setNewPresetId('merah');
    else if (lower.includes('kuning')) setNewPresetId('kuning');
    else if (lower.includes('biru')) setNewPresetId('biru');
    else if (lower.includes('hijau')) setNewPresetId('hijau');
    else if (lower.includes('guru') || lower.includes('pengerusi') || lower.includes('kepimpinan') || lower.includes('emas')) setNewPresetId('emas');
  };

  const handleConfirmAddRank = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!isUserSuperAdmin) {
      showToast('error', 'Akses Ditolak', 'Hanya Superadmin dibenarkan menambah pangkat bengkung.');
      return;
    }

    const cleanCode = newCode.trim();
    if (!cleanCode) {
      showToast('error', 'Maklumat Tidak Lengkap', 'Sila masukkan Kod / Pangkat Bengkung (cth: Kuning 8).');
      return;
    }

    const exists = bengkungList.some(r => r.code.toLowerCase() === cleanCode.toLowerCase());
    if (exists) {
      showToast('error', 'Pangkat Sudah Wujud', `Pangkat bengkung "${cleanCode}" sudah wujud dalam senarai.`);
      return;
    }

    setIsSubmittingAdd(true);
    try {
      const preset = BELT_COLOR_PRESETS.find(p => p.id === newPresetId) || BELT_COLOR_PRESETS[3];
      const cleanDbCode = cleanCode.toLowerCase().replace(/[^a-z0-9_]/g, '') || `bengkung_${Date.now()}`;
      const newRank: BengkungRankInfo = {
        code: cleanCode,
        dbCode: cleanDbCode,
        bengkungName: newBengkungName.trim() || `Bengkung ${cleanCode}`,
        rankTitle: newRankTitle.trim() || 'Pesilat',
        module: newModule,
        culaCount: Math.max(0, Math.min(12, Number(newCulaCount) || 0)),
        beltBgClass: preset.beltBgClass,
        culaBgClass: preset.culaBgClass,
        textColorClass: preset.textColorClass
      };

      const copy = [...bengkungList];
      if (newInsertPos === 'start') {
        copy.unshift(newRank);
      } else if (newInsertPos === 'after') {
        const afterIdx = copy.findIndex(r => r.code === newAfterCode);
        if (afterIdx >= 0) {
          copy.splice(afterIdx + 1, 0, newRank);
        } else {
          copy.push(newRank);
        }
      } else {
        copy.push(newRank);
      }

      await saveBengkungRanksDB(copy, currentUser?.name || 'Superadmin');
      setBengkungList(copy);
      setIsAddModalOpen(false);
      showToast(
        'success',
        'Pangkat Berjaya Ditambah!',
        `Pangkat "${cleanCode}" (${newRank.rankTitle}) telah dimasukkan ke dalam hierarki rasmi ASMM.`
      );
    } catch {
      showToast('error', 'Ralat', 'Gagal menambah pangkat bengkung.');
    } finally {
      setIsSubmittingAdd(false);
    }
  };

  const handleOpenEditModal = (rank: BengkungRankInfo) => {
    if (!isUserSuperAdmin) return;
    setRankToEdit(rank);
    setEditCode(rank.code);
    setEditRankTitle(rank.rankTitle);
    setEditBengkungName(rank.bengkungName || `Bengkung ${rank.code}`);
    setEditModule(rank.module || 'Modul Bengkung Kuning');
    setEditCulaCount(rank.culaCount ?? 0);

    const matchedPreset = BELT_COLOR_PRESETS.find(p => p.beltBgClass === rank.beltBgClass)
      || BELT_COLOR_PRESETS.find(p => p.module === rank.module)
      || BELT_COLOR_PRESETS.find(p => rank.code.toLowerCase().includes(p.id))
      || BELT_COLOR_PRESETS[3];
    setEditPresetId(matchedPreset.id);
    setIsEditModalOpen(true);
  };

  const handleEditModuleSelect = (mod: string) => {
    setEditModule(mod);
    const lower = mod.toLowerCase();
    if (lower.includes('hitam')) setEditPresetId('hitam');
    else if (lower.includes('putih')) setEditPresetId('putih');
    else if (lower.includes('merah')) setEditPresetId('merah');
    else if (lower.includes('kuning')) setEditPresetId('kuning');
    else if (lower.includes('biru')) setEditPresetId('biru');
    else if (lower.includes('hijau')) setEditPresetId('hijau');
    else if (lower.includes('guru') || lower.includes('pengerusi') || lower.includes('kepimpinan') || lower.includes('emas')) setEditPresetId('emas');
  };

  const handleConfirmEditRank = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!isUserSuperAdmin || !rankToEdit) return;

    const cleanCode = editCode.trim();
    if (!cleanCode) {
      showToast('error', 'Maklumat Tidak Lengkap', 'Sila masukkan Kod / Pangkat Bengkung (cth: Kuning 8).');
      return;
    }

    const isDuplicate = bengkungList.some(
      r => r.code.toLowerCase() !== rankToEdit.code.toLowerCase() && r.code.toLowerCase() === cleanCode.toLowerCase()
    );
    if (isDuplicate) {
      showToast('error', 'Pangkat Sudah Wujud', `Pangkat bengkung "${cleanCode}" sudah wujud dalam senarai.`);
      return;
    }

    setIsSubmittingEdit(true);
    try {
      const preset = BELT_COLOR_PRESETS.find(p => p.id === editPresetId) || BELT_COLOR_PRESETS[3];
      const cleanDbCode = cleanCode.toLowerCase().replace(/[^a-z0-9_]/g, '') || rankToEdit.dbCode;

      const updatedRank: BengkungRankInfo = {
        ...rankToEdit,
        code: cleanCode,
        dbCode: cleanDbCode,
        bengkungName: editBengkungName.trim() || `Bengkung ${cleanCode}`,
        rankTitle: editRankTitle.trim() || 'Pesilat',
        module: editModule,
        culaCount: Math.max(0, Math.min(12, Number(editCulaCount) || 0)),
        beltBgClass: preset.beltBgClass,
        culaBgClass: preset.culaBgClass,
        textColorClass: preset.textColorClass
      };

      const updated = bengkungList.map(r => r.code === rankToEdit.code ? updatedRank : r);

      await saveBengkungRanksDB(updated, currentUser?.name || 'Superadmin');
      setBengkungList(updated);
      setIsEditModalOpen(false);
      setRankToEdit(null);
      showToast(
        'success',
        'Pangkat Berjaya Dikemaskini!',
        `Pangkat "${cleanCode}" (${updatedRank.rankTitle}) telah dikemaskini.`
      );
    } catch {
      showToast('error', 'Ralat', 'Gagal mengemaskini pangkat bengkung.');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleOpenDeleteModal = (rank: BengkungRankInfo) => {
    if (!isUserSuperAdmin) return;
    if (bengkungList.length <= 1) {
      showToast('error', 'Tidak Dibenarkan', 'Sekurang-kurangnya satu pangkat bengkung mesti dikekalkan.');
      return;
    }
    setRankToDelete(rank);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDeleteRank = async () => {
    if (!isUserSuperAdmin || !rankToDelete) return;
    setIsSubmittingDelete(true);
    try {
      const updated = bengkungList.filter(r => r.code !== rankToDelete.code && r.dbCode !== rankToDelete.dbCode);
      await saveBengkungRanksDB(updated, currentUser?.name || 'Superadmin');
      setBengkungList(updated);
      setIsDeleteModalOpen(false);
      showToast(
        'success',
        'Pangkat Berjaya Dipadam',
        `Pangkat "${rankToDelete.code}" (${rankToDelete.rankTitle}) telah dikeluarkan daripada hierarki.`
      );
      setRankToDelete(null);
    } catch {
      showToast('error', 'Ralat', 'Gagal memadam pangkat bengkung.');
    } finally {
      setIsSubmittingDelete(false);
    }
  };

  // --- FEE CONFIG HANDLERS ---
  const handleModulePriceChange = (moduleKey: 'hitam' | 'putih' | 'merah' | 'biru' | 'kuning', val: number) => {
    const validVal = isNaN(val) ? 0 : Math.max(0, val);
    setFeeConfig(prev => {
      const updatedCustom = { ...(prev.customLevels || {}) };
      bengkungList.forEach(r => {
        const norm = r.code.toLowerCase();
        if (norm.includes(moduleKey)) {
          updatedCustom[r.code] = validVal;
        }
      });
      return {
        ...prev,
        [moduleKey]: validVal,
        customLevels: updatedCustom
      };
    });
  };

  const handleQuickAdjustModule = (moduleKey: 'hitam' | 'putih' | 'merah' | 'biru' | 'kuning', delta: number) => {
    setFeeConfig(prev => {
      const current = prev[moduleKey] ?? 50;
      const nextVal = Math.max(0, current + delta);
      const updatedCustom = { ...(prev.customLevels || {}) };
      bengkungList.forEach(r => {
        const norm = r.code.toLowerCase();
        if (norm.includes(moduleKey)) {
          updatedCustom[r.code] = nextVal;
        }
      });
      return {
        ...prev,
        [moduleKey]: nextVal,
        customLevels: updatedCustom
      };
    });
  };

  const handleSaveFees = async () => {
    if (!isUserSuperAdmin) {
      showToast('error', 'Akses Ditolak', 'Hanya Superadmin dibenarkan menyimpan perubahan tetapan yuran.');
      return;
    }
    setIsSaving(true);
    try {
      await saveUjianBengkungFeesDB(feeConfig, currentUser?.name || 'Superadmin');
      showToast(
        'success',
        'Tetapan Yuran Berjaya Disimpan!',
        'Yuran ujian bengkung telah dikemaskini. Invois pendaftaran calon akan menggunakan kadar baharu ini secara automatik.'
      );
    } catch {
      showToast('error', 'Ralat', 'Gagal menyimpan tetapan yuran ke pangkalan data.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetFeesToDefault = () => {
    setFeeConfig(DEFAULT_UJIAN_BENGKUNG_FEES);
    showToast('info', 'Tetapan Ditetapkan Semula', 'Kadar yuran telah dipulihkan kepada nilai piawaian rasmi ASMM.');
  };

  // Group ranks by module for fee configuration
  const ranksByModule = useMemo(() => {
    const grouped: Record<string, BengkungRankInfo[]> = {
      'Hitam': [],
      'Putih': [],
      'Merah': [],
      'Biru': [],
      'Kuning': []
    };

    bengkungList.forEach(r => {
      const code = (r.code || '').toLowerCase();
      const mod = (r.module || '').toLowerCase();
      if (code.includes('hitam') || mod.includes('hitam')) grouped['Hitam'].push(r);
      else if (code.includes('putih') || mod.includes('putih')) grouped['Putih'].push(r);
      else if (code.includes('merah') || mod.includes('merah')) grouped['Merah'].push(r);
      else if (code.includes('biru') || mod.includes('biru')) grouped['Biru'].push(r);
      else if (code.includes('kuning') || mod.includes('kuning')) grouped['Kuning'].push(r);
      else grouped['Putih'].push(r);
    });

    return grouped;
  }, [bengkungList]);

  // Filtered bengkung list for order display
  const filteredBengkungList = useMemo(() => {
    if (bengkungFilterModule === 'all') return bengkungList;
    return bengkungList.filter(r => {
      const mod = (r.module || '').toLowerCase();
      const code = (r.code || '').toLowerCase();
      return mod.includes(bengkungFilterModule.toLowerCase()) || code.includes(bengkungFilterModule.toLowerCase());
    });
  }, [bengkungList, bengkungFilterModule]);


  if (!isUserSuperAdmin) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4 text-center space-y-4">
        <div className="w-16 h-16 bg-rose-100 text-rose-600 rounded-3xl mx-auto flex items-center justify-center shadow-inner">
          <ShieldAlert className="w-8 h-8" />
        </div>
        <h2 className="text-lg font-black text-slate-900">Akses Terhad (Superadmin Sahaja)</h2>
        <p className="text-xs text-slate-600 leading-relaxed max-w-md mx-auto">
          Halaman Tetapan Sistem dan penetapan yuran ujian Bengkung adalah terhad kepada akaun <strong>Superadmin Pertubuhan</strong> sahaja.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn pb-16">
      <PageHeader
        badgeText="Superadmin Sahaja"
        badgeIcon={ShieldCheck}
        title={
          activeMainTab === 'alert'
            ? 'Tetapan Alert & PWA Push Notification'
            : activeMainTab === 'payment'
              ? 'Tetapan Pembayaran & QR Peringkat Persatuan'
              : activeMainTab === 'bengkung'
                ? 'Tetapan Sistem & Hierarki Bengkung'
                : 'Tetapan Kadar Yuran Ujian Bengkung'
        }
      >
        <div className="flex items-center gap-2">
          {activeMainTab === 'alert' ? (
            <>
              <button
                type="button"
                onClick={handleResetAlertSettings}
                disabled={isSavingAlert}
                className="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs active:scale-95"
                title="Pulihkan tetapan asal 20 jam ASMM"
              >
                <RotateCcw className="w-3.5 h-3.5 text-slate-400" />
                <span className="hidden sm:inline">Set Semula Lalai</span>
              </button>
              <button
                type="button"
                onClick={handleSaveAlertSettings}
                disabled={isSavingAlert}
                className="px-4 py-2 bg-asmm-red hover:bg-red-700 text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                <Save className="w-3.5 h-3.5" />
                <span>{isSavingAlert ? 'Menyimpan...' : 'Simpan Tetapan Alert'}</span>
              </button>
            </>
          ) : activeMainTab === 'payment' ? (
            <>
              <button
                type="button"
                onClick={handleResetPaymentSettings}
                disabled={isSavingPayment}
                className="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs active:scale-95"
                title="Pulihkan tetapan akaun piawai persatuan"
              >
                <RotateCcw className="w-3.5 h-3.5 text-slate-400" />
                <span className="hidden sm:inline">Set Semula Asal</span>
              </button>
              <button
                type="button"
                onClick={handleSavePaymentSettings}
                disabled={isSavingPayment}
                className="px-4 py-2 bg-asmm-red hover:bg-red-700 text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                <Save className="w-3.5 h-3.5" />
                <span>{isSavingPayment ? 'Menyimpan...' : 'Simpan Tetapan Bayaran'}</span>
              </button>
            </>
          ) : activeMainTab === 'bengkung' ? (
            <>
              <button
                type="button"
                onClick={handleResetBengkungList}
                disabled={isSavingBengkung}
                className="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs active:scale-95"
                title="Pulihkan susunan & nama piawai ASMM"
              >
                <RotateCcw className="w-3.5 h-3.5 text-slate-400" />
                <span className="hidden sm:inline">Set Semula Asal</span>
              </button>
              <button
                type="button"
                onClick={handleSaveBengkungList}
                disabled={isSavingBengkung}
                className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                <Save className="w-3.5 h-3.5" />
                <span>{isSavingBengkung ? 'Menyimpan...' : 'Simpan Susunan'}</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleResetFeesToDefault}
                disabled={isSaving}
                className="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs active:scale-95"
                title="Pulihkan nilai piawai asal"
              >
                <RotateCcw className="w-3.5 h-3.5 text-slate-400" />
                <span className="hidden sm:inline">Set Semula Lalai</span>
              </button>
              <button
                type="button"
                onClick={handleSaveFees}
                disabled={isSaving}
                className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                <Save className="w-3.5 h-3.5" />
                <span>{isSaving ? 'Menyimpan...' : 'Simpan Yuran'}</span>
              </button>
            </>
          )}
        </div>
      </PageHeader>

      {/* Main Section Navigation Pills */}
      <div className="flex items-center gap-1.5 sm:gap-2 bg-slate-100 p-1.5 rounded-2xl w-full max-w-2xl shadow-inner border border-slate-200">
        <button
          type="button"
          onClick={() => handleTabSwitch('alert')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-2.5 sm:px-3 rounded-xl text-xs sm:text-sm font-black transition-all cursor-pointer ${activeMainTab === 'alert'
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-600 hover:text-slate-900 hover:bg-white/40'
            }`}
        >
          <BellRing className={`w-4 h-4 ${activeMainTab === 'alert' ? 'text-asmm-red' : 'text-slate-400'}`} />
          <span>Alert &amp; Notifikasi</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabSwitch('payment')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-2.5 sm:px-3 rounded-xl text-xs sm:text-sm font-black transition-all cursor-pointer ${activeMainTab === 'payment'
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-600 hover:text-slate-900 hover:bg-white/40'
            }`}
        >
          <CreditCard className={`w-4 h-4 ${activeMainTab === 'payment' ? 'text-amber-500' : 'text-slate-400'}`} />
          <span>Pembayaran Persatuan</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabSwitch('bengkung')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-2.5 sm:px-3 rounded-xl text-xs sm:text-sm font-black transition-all cursor-pointer ${activeMainTab === 'bengkung'
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-600 hover:text-slate-900 hover:bg-white/40'
            }`}
        >
          <ListOrdered className={`w-4 h-4 ${activeMainTab === 'bengkung' ? 'text-blue-600' : 'text-slate-400'}`} />
          <span>Bengkung</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabSwitch('yuran')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-2.5 sm:px-3 rounded-xl text-xs sm:text-sm font-black transition-all cursor-pointer ${activeMainTab === 'yuran'
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-600 hover:text-slate-900 hover:bg-white/40'
            }`}
        >
          <Award className={`w-4 h-4 ${activeMainTab === 'yuran' ? 'text-slate-900' : 'text-slate-400'}`} />
          <span>Yuran Ujian</span>
        </button>
      </div>

      {/* TAB 0: ALERT & PWA NOTIFICATIONS */}
      {activeMainTab === 'alert' && (
        <div className="space-y-6">
          {/* Top Hero Banner & Metric Pill Bar */}
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-8 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-asmm-red text-white flex items-center justify-center shadow-md ring-4 ring-red-50 shrink-0">
                  <BellRing className="w-7 h-7 animate-pulse" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Pusat Kawalan Alert &amp; Notifikasi PWA
                    </h2>
                    <span className="text-[10px] font-black bg-red-100 text-asmm-red px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                      Superadmin Sahaja
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">
                    Sesuaikan gelagat Web Push Toast peranti mudah alih &amp; jadual peringatan automatik 20 jam sebelum sesi gelanggang.
                  </p>
                </div>
              </div>

              {/* Status Badge */}
              <div className="flex items-center gap-2 self-start md:self-auto bg-slate-50 border border-slate-200/80 px-3.5 py-2 rounded-2xl">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                <span className="text-[11px] font-bold text-slate-700">
                  aaPanel Cron Aktif (103.75.191.32)
                </span>
              </div>
            </div>

            {/* Floating Metric Pill Bar */}
            <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-200/60 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="space-y-0.5">
                <p className="text-xs font-semibold text-slate-500">Masa Peringatan RSVP</p>
                <p className="text-lg sm:text-xl font-black text-asmm-red">{alertSettings.rsvpLeadHours} Jam</p>
                <p className="text-[10px] text-slate-400 font-medium">Sebelum Sesi Latihan</p>
              </div>
              <div className="space-y-0.5 border-l border-slate-200/80">
                <p className="text-xs font-semibold text-slate-500">Gelanggang Sasaran</p>
                <p className="text-lg sm:text-xl font-black text-amber-500">{alertSettings.targetGelanggangs.length} Lokasi</p>
                <p className="text-[10px] text-slate-400 font-medium">Dipantau Automatik</p>
              </div>
              <div className="space-y-0.5 border-l border-slate-200/80">
                <p className="text-xs font-semibold text-slate-500">Protokol Web Push</p>
                <p className="text-lg sm:text-xl font-black text-blue-600">RFC 8292</p>
                <p className="text-[10px] text-slate-400 font-medium">Standard VAPID</p>
              </div>
              <div className="space-y-0.5 border-l border-slate-200/80">
                <p className="text-xs font-semibold text-slate-500">Status PWA Push</p>
                <p className={`text-lg sm:text-xl font-black ${alertSettings.pwaPushEnabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {alertSettings.pwaPushEnabled ? 'DIBENARKAN' : 'DIMATIKAN'}
                </p>
                <p className="text-[10px] text-slate-400 font-medium">Sistem Utama</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Configuration Controls (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              {/* Card 1: Master Push & Sesi Gelanggang RSVP Timing */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-6">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-red-50 text-asmm-red rounded-2xl border border-red-100">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-extrabold text-slate-900">
                        Peringatan Sesi Latihan Gelanggang (RSVP Practice)
                      </h3>
                      <p className="text-[11px] text-slate-500 font-medium">
                        Kawal masa bilakah pesilat menerima toast notifikasi sebelum latihan bermula.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Master PWA Push Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
                  <div className="space-y-0.5 pr-4">
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-asmm-red" />
                      <span className="text-xs font-extrabold text-slate-900">
                        Aktifkan Enjin PWA Web Push Mobile
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Membenarkan peranti ahli (Android &amp; iOS PWA) menerima notifikasi latar belakang.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAlertSettings(prev => ({ ...prev, pwaPushEnabled: !prev.pwaPushEnabled }))}
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${alertSettings.pwaPushEnabled ? 'bg-asmm-red' : 'bg-slate-300'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${alertSettings.pwaPushEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>

                {/* Auto Gelanggang Notification Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
                  <div className="space-y-0.5 pr-4">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-extrabold text-slate-900">
                        Automasi Peringatan RSVP Sesi Latihan
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Cron aaPanel menyemak jadual dan menghantar alert automatik kepada ahli berkaitan.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAlertSettings(prev => ({ ...prev, autoNotifyGelanggang: !prev.autoNotifyGelanggang }))}
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${alertSettings.autoNotifyGelanggang ? 'bg-amber-500' : 'bg-slate-300'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${alertSettings.autoNotifyGelanggang ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>

                {/* Lead Hours Setting */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-extrabold text-slate-900 flex items-center gap-1.5">
                      <span>Tempoh Amaran Sebelum Sesi (Lead Time):</span>
                      <span className="px-2 py-0.5 bg-red-100 text-asmm-red font-black rounded-lg text-xs">
                        {alertSettings.rsvpLeadHours} Jam Sebelum
                      </span>
                    </label>
                  </div>

                  {/* Preset Pills */}
                  <div className="grid grid-cols-4 gap-2">
                    {[12, 18, 20, 24].map((hrs) => (
                      <button
                        key={hrs}
                        type="button"
                        onClick={() => setAlertSettings(prev => ({ ...prev, rsvpLeadHours: hrs }))}
                        className={`py-2 px-3 rounded-xl text-xs font-black transition-all cursor-pointer border ${alertSettings.rsvpLeadHours === hrs
                          ? 'bg-asmm-red text-white border-asmm-red shadow-sm'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                      >
                        {hrs} Jam {hrs === 20 ? '★' : ''}
                      </button>
                    ))}
                  </div>

                  <p className="text-[11px] text-slate-500 font-medium">
                    Contoh: Jika sesi latihan Jumaat bermula jam 8:30 PM, ahli akan menerima notifikasi toast pada telefon mereka tepat jam <strong>12:30 AM tengah malam sebelumnya (20 jam lebih awal)</strong> untuk mengesahkan kehadiran (RSVP).
                  </p>
                </div>

                {/* Target Gelanggangs */}
                <div className="space-y-3">
                  <label className="text-xs font-extrabold text-slate-900">
                    Gelanggang Yang Terlibat Dalam Peringatan Automatik:
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {(['Pusaka Warisan', 'Wadi AnNour', 'Paya Besar'] as Gelanggang[]).map((gName) => {
                      const isSelected = alertSettings.targetGelanggangs.includes(gName);
                      return (
                        <button
                          key={gName}
                          type="button"
                          onClick={() => {
                            setAlertSettings(prev => {
                              const exists = prev.targetGelanggangs.includes(gName);
                              return {
                                ...prev,
                                targetGelanggangs: exists
                                  ? prev.targetGelanggangs.filter(g => g !== gName)
                                  : [...prev.targetGelanggangs, gName]
                              };
                            });
                          }}
                          className={`p-3 rounded-2xl text-left border transition-all cursor-pointer flex items-center justify-between ${isSelected
                            ? 'bg-red-50/60 border-asmm-red/60 text-slate-900 shadow-2xs'
                            : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                            }`}
                        >
                          <div>
                            <p className="text-xs font-black">{gName}</p>
                            <p className="text-[10px] text-slate-400 font-medium">Aktif RSVP</p>
                          </div>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isSelected ? 'bg-asmm-red text-white' : 'border border-slate-300'}`}>
                            {isSelected && <Check className="w-3 h-3 stroke-3" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Card 2: Message Template & Interactive Buttons */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-6">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="p-2.5 bg-blue-50 text-blue-600 rounded-2xl border border-blue-100">
                    <Send className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900">
                      Templat Mesej &amp; Butang Aksi Telefon
                    </h3>
                    <p className="text-[11px] text-slate-500 font-medium">
                      Format perkataan yang dipaparkan dalam notification bar dan lockscreen telefon.
                    </p>
                  </div>
                </div>

                {/* Event Type Selector */}
                <div className="space-y-2">
                  <label className="text-xs font-extrabold text-slate-900">
                    Jenis Acara
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {ALERT_EVENT_TYPES.map((t) => (
                      <button
                        key={t.key}
                        id={`alert-tpl-type-${t.key}`}
                        type="button"
                        onClick={() => setActiveTplType(t.key)}
                        className={`px-3.5 py-1.5 rounded-full text-[11px] font-extrabold border transition-all cursor-pointer ${activeTplType === t.key
                          ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                          }`}
                      >
                        {t.emoji} {t.label}
                      </button>
                    ))}
                  </div>
                  {activeTplType === 'latihan' && (
                    <p className="text-[10px] text-slate-500 font-medium">
                      Templat ini digunakan oleh cron aaPanel secara automatik ({alertSettings.rsvpLeadHours} jam sebelum sesi).
                    </p>
                  )}
                </div>

                {/* Title Template */}
                <div className="space-y-1.5">
                  <label className="text-xs font-extrabold text-slate-900">
                    Format Tajuk Notifikasi (Title)
                  </label>
                  <input
                    id="alert-tpl-title"
                    type="text"
                    value={activeTpl.title}
                    onChange={(e) => updateActiveTpl('title', e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-asmm-red"
                    placeholder={DEFAULT_EVENT_TEMPLATES[activeTplType].title}
                  />
                </div>

                {/* Body Template */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-extrabold text-slate-900">
                      Format Kandungan Mesej (Body)
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        updateActiveTpl('title', DEFAULT_EVENT_TEMPLATES[activeTplType].title);
                        updateActiveTpl('body', DEFAULT_EVENT_TEMPLATES[activeTplType].body);
                      }}
                      className="text-[10px] font-bold text-slate-500 hover:text-asmm-red bg-transparent border-none cursor-pointer"
                    >
                      Pulihkan Templat Asal
                    </button>
                  </div>
                  <textarea
                    id="alert-tpl-body"
                    ref={bodyTplRef}
                    rows={3}
                    value={activeTpl.body}
                    onChange={(e) => updateActiveTpl('body', e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-asmm-red"
                    placeholder={DEFAULT_EVENT_TEMPLATES[activeTplType].body}
                  />
                  {/* Dynamic Variable Chips (click to insert at cursor) */}
                  <div className="flex items-center gap-1.5 flex-wrap pt-1">
                    <span className="text-[10px] text-slate-400 font-bold">Klik untuk masukkan:</span>
                    {ALERT_TEMPLATE_VARIABLES.map((v) => (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() => insertTplVariable(v.key)}
                        className={`px-2 py-0.5 text-[10px] font-mono font-bold rounded-md border cursor-pointer transition ${v.key === '{bila}' || v.key === '{tarikh}'
                          ? 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                          }`}
                        title={v.desc}
                      >
                        {v.key}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500 leading-relaxed">
                    <strong className="text-slate-700">{'{bila}'}</strong> dikira automatik ikut tarikh acara: <em>hari ini</em>, <em>esok</em>, <em>lusa</em>, <em>dalam 4 hari</em>, <em>minggu depan</em>. Jangan tulis "esok" secara tetap.
                  </p>
                </div>

                {/* Interactive Action Buttons */}
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-extrabold text-slate-900">
                        Butang Tindakan Terus (Action Buttons pada Notifikasi)
                      </span>
                      <p className="text-[11px] text-slate-500">
                        Membolehkan pesilat klik terus "Hadir" atau "Cuti" dari lockscreen tanpa membuka browser.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAlertSettings(prev => ({ ...prev, enableActionButtons: !prev.enableActionButtons }))}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${alertSettings.enableActionButtons ? 'bg-blue-600' : 'bg-slate-300'}`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${alertSettings.enableActionButtons ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>

                  {alertSettings.enableActionButtons && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-slate-600">Label Butang 1 (Sahkan Hadir):</label>
                        <input
                          type="text"
                          value={alertSettings.actionConfirmLabel}
                          onChange={(e) => setAlertSettings(prev => ({ ...prev, actionConfirmLabel: e.target.value }))}
                          className="w-full px-3 py-2 bg-white rounded-xl border border-slate-200 text-xs font-bold text-slate-800"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-slate-600">Label Butang 2 (Tidak Hadir):</label>
                        <input
                          type="text"
                          value={alertSettings.actionDeclineLabel}
                          onChange={(e) => setAlertSettings(prev => ({ ...prev, actionDeclineLabel: e.target.value }))}
                          className="w-full px-3 py-2 bg-white rounded-xl border border-slate-200 text-xs font-bold text-slate-800"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Card 3: Additional Alert Types & Haptic Vibration */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="p-2.5 bg-amber-50 text-amber-600 rounded-2xl border border-amber-100">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900">
                      Jenis Amaran Tambahan &amp; Getaran Telefon
                    </h3>
                    <p className="text-[11px] text-slate-500 font-medium">
                      Pengumuman kecemasan, peringatan yuran bengkung &amp; haptic feedback.
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80">
                    <div className="space-y-0.5">
                      <span className="text-xs font-extrabold text-slate-900">Peringatan Yuran Bulanan / Bengkung</span>
                      <p className="text-[11px] text-slate-500">Hantar push notis bagi invois belum berbayar 3 hari sebelum tarikh akhir.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAlertSettings(prev => ({ ...prev, notifyFeeReminders: !prev.notifyFeeReminders }))}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${alertSettings.notifyFeeReminders ? 'bg-amber-500' : 'bg-slate-300'}`}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${alertSettings.notifyFeeReminders ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80">
                    <div className="space-y-0.5">
                      <span className="text-xs font-extrabold text-slate-900">Pengumuman Rasmi Persatuan (Broadcast Alert)</span>
                      <p className="text-[11px] text-slate-500">Notis penutupan gelanggang mengejut / kenduri / mesyuarat agung.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAlertSettings(prev => ({ ...prev, notifyGeneralAnnouncements: !prev.notifyGeneralAnnouncements }))}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${alertSettings.notifyGeneralAnnouncements ? 'bg-blue-600' : 'bg-slate-300'}`}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${alertSettings.notifyGeneralAnnouncements ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80">
                    <div className="space-y-0.5">
                      <span className="text-xs font-extrabold text-slate-900">Getaran Telefon Pintar (Haptic Vibration)</span>
                      <p className="text-[11px] text-slate-500">Corak getaran [200ms, 100ms, 200ms] semasa notifikasi masuk.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAlertSettings(prev => ({ ...prev, enableVibration: !prev.enableVibration }))}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${alertSettings.enableVibration ? 'bg-asmm-red' : 'bg-slate-300'}`}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${alertSettings.enableVibration ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Live Push Testing, Mockup Preview & aaPanel Cron Details (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              {/* Card 4: Device Push Testing */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="p-2.5 bg-red-50 text-asmm-red rounded-2xl border border-red-100">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900">
                      Ujian Notifikasi Langsung (Live Test)
                    </h3>
                    <p className="text-[11px] text-slate-500 font-medium">
                      Uji paparan toast Web Push pada peranti anda sekarang.
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-3">
                  <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                    <span>Sokongan Pelayar Ini:</span>
                    <span className="text-emerald-600 font-black flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Disokong
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                    <span>Kebenaran Notifikasi:</span>
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-white border border-slate-200 text-slate-800">
                      {typeof Notification !== 'undefined' ? Notification.permission : 'Standard'}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleTestPushNotification}
                  disabled={isTestingPush}
                  className="w-full py-3 px-4 bg-asmm-red hover:bg-red-700 active:scale-98 text-white rounded-2xl text-xs font-extrabold transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  <BellRing className="w-4 h-4" />
                  <span>{isTestingPush ? 'Menghantar Ujian...' : 'Uji Notifikasi Toast PWA Sekarang'}</span>
                </button>
              </div>

              {/* Card 5: Live Smartphone Mockup */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <span className="text-xs font-black text-slate-900 uppercase tracking-wider">
                    Pratonton Skrin Telefon Pintar
                  </span>
                  <span className="text-[10px] font-bold text-slate-400">Lockscreen Live</span>
                </div>

                {/* Sample event date selector */}
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 block">Contoh tarikh acara:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { d: 0, label: 'Hari ini' },
                      { d: 1, label: 'Esok' },
                      { d: 2, label: 'Lusa' },
                      { d: 4, label: '4 hari' },
                      { d: 9, label: 'Minggu depan' },
                    ].map(o => (
                      <button
                        key={o.d}
                        type="button"
                        onClick={() => setPreviewOffsetDays(o.d)}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold border transition cursor-pointer ${previewOffsetDays === o.d
                          ? 'bg-asmm-red text-white border-asmm-red'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                          }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-400 font-medium">
                    {formatShortDate(previewVars.eventDate)} → {'{bila}'} = <strong className="text-slate-700">{formatRelativeDay(previewVars.eventDate)}</strong>
                  </p>
                </div>

                {/* Smartphone Bezel */}
                <div className="w-full max-w-xs mx-auto bg-slate-900 rounded-[2.5rem] p-3 shadow-xl border-4 border-slate-800">
                  {/* Speaker & Camera Notch */}
                  <div className="w-20 h-3.5 bg-black rounded-full mx-auto mb-3"></div>

                  {/* Screen Content */}
                  <div className="rounded-[1.75rem] bg-gradient-to-b from-slate-950 via-slate-900 to-red-950/70 p-4 min-h-[340px] flex flex-col justify-between text-white relative overflow-hidden">
                    {/* Lockscreen Time */}
                    <div className="text-center pt-2 space-y-0.5">
                      <p className="text-[10px] font-semibold text-slate-400 tracking-wider">Khamis, 15 Okt</p>
                      <p className="text-4xl font-extralight tracking-tight">08:30</p>
                    </div>

                    {/* Notification Banner */}
                    <div className="my-auto space-y-2">
                      <div className="bg-white/95 text-slate-900 backdrop-blur-md rounded-2xl p-3 shadow-lg border border-white/40 space-y-2 animate-fadeIn">
                        {/* Notification Header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <img src="/logo-pssasmm.png" alt="ASMM" className="w-4 h-4 rounded-full object-cover" />
                            <span className="text-[10px] font-black tracking-tight text-slate-900">PSSASMM PWA</span>
                          </div>
                          <span className="text-[9px] font-bold text-slate-400">Sekarang</span>
                        </div>

                        {/* Title & Body from State */}
                        <div className="space-y-0.5">
                          <p className="text-[11px] font-black text-slate-900 leading-tight">
                            {previewTitle}
                          </p>
                          <p className="text-[10px] text-slate-600 leading-relaxed font-medium">
                            {previewBody}
                          </p>
                        </div>

                        {/* Interactive Buttons */}
                        {alertSettings.enableActionButtons && (
                          <div className="grid grid-cols-2 gap-1.5 pt-1 border-t border-slate-100">
                            <button
                              type="button"
                              className="py-1 px-2 bg-asmm-red text-white text-[10px] font-extrabold rounded-lg text-center shadow-xs"
                            >
                              {alertSettings.actionConfirmLabel}
                            </button>
                            <button
                              type="button"
                              className="py-1 px-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-bold rounded-lg text-center"
                            >
                              {alertSettings.actionDeclineLabel}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bottom Home Indicator */}
                    <div className="w-24 h-1 bg-white/40 rounded-full mx-auto mb-1"></div>
                  </div>
                </div>
              </div>

              {/* Card 6: aaPanel Server Cron & VAPID Details */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="p-2.5 bg-slate-100 text-slate-800 rounded-2xl border border-slate-200">
                    <Server className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900">
                      Konfigurasi aaPanel VPS &amp; Cron
                    </h3>
                    <p className="text-[11px] text-slate-500 font-medium">
                      Pelayan WargaLeads (103.75.191.32 / leads.wargabiz.com.my)
                    </p>
                  </div>
                </div>

                {/* VAPID Public Key */}
                <div className="space-y-1.5">
                  <label className="text-[11px] font-extrabold text-slate-700 flex items-center justify-between">
                    <span>VAPID Public Key:</span>
                    <button
                      type="button"
                      onClick={handleCopyVapidKey}
                      className="text-asmm-red hover:underline text-[10px] flex items-center gap-1 cursor-pointer font-bold"
                    >
                      {copiedVapid ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      <span>{copiedVapid ? 'Disalin!' : 'Salin Kunci'}</span>
                    </button>
                  </label>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-200 font-mono text-[10px] text-slate-700 break-all select-all">
                    {alertSettings.vapidPublicKey}
                  </div>
                </div>

                {/* aaPanel Cron Snippet */}
                <div className="space-y-1.5">
                  <label className="text-[11px] font-extrabold text-slate-700 flex items-center gap-1">
                    <Terminal className="w-3 h-3 text-slate-500" />
                    <span>Arahan Cron aaPanel (Jadual Setiap Jam):</span>
                  </label>
                  <div className="p-2.5 bg-slate-900 text-slate-100 rounded-xl font-mono text-[10px] break-all select-all">
                    0 * * * * php /www/wwwroot/leads.wargabiz.com.my/api-asmm/cron_notify_gelanggang.php &gt; /dev/null 2&gt;&amp;1
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 0.5: PERSATUAN LEVEL PAYMENT & QR SETTINGS */}
      {activeMainTab === 'payment' && (
        <div className="space-y-6">
          {/* Top Hero Banner & Metric Pill Bar */}
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-8 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-amber-500 text-white flex items-center justify-center shadow-md ring-4 ring-amber-50 shrink-0">
                  <CreditCard className="w-7 h-7" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Akaun Bank &amp; Gateway Pembayaran Peringkat Persatuan
                    </h2>
                    <span className="text-[10px] font-black bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                      Akaun Induk Rasmi
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">
                    Tetapan akaun rasmi induk PSSASMM bagi menerima kutipan Infaq 'Semua Gelanggang', bayaran vendor sewa tapak, dan yuran persatuan.
                  </p>
                </div>
              </div>

              {/* ROS Credentials Badge */}
              <div className="flex items-center gap-2 self-start md:self-auto bg-slate-50 border border-slate-200/80 px-3.5 py-2 rounded-2xl">
                <ShieldCheck className="w-4 h-4 text-asmm-red shrink-0" />
                <span className="text-[11px] font-bold text-slate-700">
                  ROS: {paymentSettings.rosRegistrationNo}
                </span>
              </div>
            </div>

            {/* Floating Metric Pill Bar */}
            <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-200/60 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="space-y-0.5">
                <p className="text-xs font-semibold text-slate-500">Bank Rasmi Induk</p>
                <p className="text-lg sm:text-xl font-black text-asmm-red">Bank Islam</p>
                <p className="text-[10px] text-slate-400 font-medium">BIMB Malaysia</p>
              </div>
              <div className="space-y-0.5 border-l border-slate-200/80">
                <p className="text-xs font-semibold text-slate-500">Gateway FPX Online</p>
                <p className="text-lg sm:text-xl font-black text-amber-500">ToyyibPay</p>
                <p className="text-[10px] text-slate-400 font-medium">{paymentSettings.fpxEnabled ? 'Aktif (Online)' : 'Dimatikan'}</p>
              </div>
              <div className="space-y-0.5 border-l border-slate-200/80">
                <p className="text-xs font-semibold text-slate-500">Aliran Infaq Am</p>
                <p className="text-lg sm:text-xl font-black text-blue-600">Semua Gelanggang</p>
                <p className="text-[10px] text-slate-400 font-medium">Terus Ke Persatuan</p>
              </div>
              <div className="space-y-0.5 border-l border-slate-200/80">
                <p className="text-xs font-semibold text-slate-500">Vendor / Pihak Ke-3</p>
                <p className="text-lg sm:text-xl font-black text-slate-900">PSSASMM</p>
                <p className="text-[10px] text-slate-400 font-medium">Kunci Akaun Induk</p>
              </div>
            </div>
          </div>

          {/* CRITICAL BUSINESS RULES BANNER */}
          <div className="bg-amber-50/70 border border-amber-200/90 rounded-3xl p-5 sm:p-6 space-y-3 shadow-2xs">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-amber-500 text-white rounded-xl shadow-xs">
                <Building2 className="w-4 h-4" />
              </div>
              <h3 className="text-xs sm:text-sm font-black text-amber-950 uppercase tracking-wide">
                Peraturan Utama Aliran Transaksi Peringkat Persatuan (Bukan Gelanggang)
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-amber-900/90 leading-relaxed font-medium pt-1">
              <div className="bg-white/80 p-3.5 rounded-2xl border border-amber-200/60 space-y-1">
                <div className="flex items-center gap-1.5 font-extrabold text-amber-950">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span>1. Infaq Penerima: 'Semua Gelanggang'</span>
                </div>
                <p className="text-[11px] text-slate-600 pl-5">
                  Sebarang infaq atau kempen tabung kebajikan yang menandakan Gelanggang Penerima sebagai <strong>"Semua Gelanggang"</strong> akan menyalurkan dana terus ke Akaun Bank &amp; DuitNow QR rasmi Persatuan di bawah, bukan ke akaun peribadi ketua gelanggang.
                </p>
              </div>

              <div className="bg-white/80 p-3.5 rounded-2xl border border-amber-200/60 space-y-1">
                <div className="flex items-center gap-1.5 font-extrabold text-amber-950">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span>2. Bayaran Vendor &amp; Pihak Ketiga: 'PSSASMM'</span>
                </div>
                <p className="text-[11px] text-slate-600 pl-5">
                  Jika pembayar adalah vendor luar, penaja syarikat, atau pihak ketiga yang membayar dengan gelanggang = <strong>"PSSASMM"</strong> (cth: sewa tapak karnival, tajaan acara rasmi), bayaran wajib didepositkan terus ke akaun rasmi induk pertubuhan ini.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Bank Account Details & Enforcements (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              {/* Card 1: Official Bank Account & DuitNow QR */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-6">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="p-2.5 bg-red-50 text-asmm-red rounded-2xl border border-red-100">
                    <Landmark className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900">
                      Maklumat Akaun Bank &amp; DuitNow QR Persatuan
                    </h3>
                    <p className="text-[11px] text-slate-500 font-medium">
                      Akaun bank rasmi yang digunakan untuk invois rasmi, pindahan manual &amp; imbasan QR.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Bank Name */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold text-slate-900">
                      Nama Bank Rasmi
                    </label>
                    <input
                      type="text"
                      value={paymentSettings.bankName}
                      onChange={(e) => setPaymentSettings(prev => ({ ...prev, bankName: e.target.value }))}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-asmm-red"
                      placeholder="Contoh: Bank Islam Malaysia Berhad"
                    />
                  </div>

                  {/* Account Number */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold text-slate-900">
                      Nombor Akaun Bank
                    </label>
                    <input
                      type="text"
                      value={paymentSettings.accountNo}
                      onChange={(e) => setPaymentSettings(prev => ({ ...prev, accountNo: e.target.value }))}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-mono font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-asmm-red"
                      placeholder="Contoh: 02011010087654"
                    />
                  </div>
                </div>

                {/* Account Holder Name */}
                <div className="space-y-1.5">
                  <label className="text-xs font-extrabold text-slate-900">
                    Nama Pemegang Akaun (Sama Seperti Surat ROS / Bank)
                  </label>
                  <input
                    type="text"
                    value={paymentSettings.accountHolder}
                    onChange={(e) => setPaymentSettings(prev => ({ ...prev, accountHolder: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-asmm-red"
                    placeholder="Contoh: PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA NEGERI KEDAH"
                  />
                </div>

                {/* DuitNow QR Image URL & Upload */}
                <div className="space-y-1.5">
                  <label className="text-xs font-extrabold text-slate-900 flex items-center justify-between">
                    <span>Imej Kod DuitNow QR Rasmi Persatuan (Path / URL):</span>
                    {paymentSettings.qrImageUrl && (
                      <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                        <Check className="w-3 h-3" /> Kod QR Dikonfigurasi
                      </span>
                    )}
                  </label>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                      type="text"
                      value={paymentSettings.qrImageUrl || ''}
                      onChange={(e) => setPaymentSettings(prev => ({ ...prev, qrImageUrl: e.target.value }))}
                      className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-asmm-red"
                      placeholder="Contoh: /badges/qr-persatuan-asmm.png"
                    />
                    <button
                      type="button"
                      onClick={() => handleDigitizeExistingUrl(paymentSettings.qrImageUrl || '')}
                      disabled={isProcessingQrSvg || !paymentSettings.qrImageUrl}
                      className="px-3.5 py-2.5 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 text-amber-800 font-bold text-xs rounded-xl transition-all border border-amber-200 flex items-center justify-center gap-1.5 shrink-0"
                      title="Ekstrak payload kod QR dan tukar kepada digital SVG 1:1"
                    >
                      {isProcessingQrSvg ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600" />
                      ) : (
                        <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                      )}
                      <span>Digitalkan SVG 1:1</span>
                    </button>
                    <label className="px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-xs rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 shrink-0 border border-slate-200">
                      <Upload className="w-3.5 h-3.5" />
                      <span>Muat Naik</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleQrFileUpload(file);
                          }
                        }}
                      />
                    </label>
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium">
                    Sistem mengekstrak payload DuitNow dan menjana format digital SVG 1:1 secara automatik untuk kejelasan maksimum seperti pada invois rasmi.
                  </p>
                </div>

                {/* Payment Instructions */}
                <div className="space-y-1.5">
                  <label className="text-xs font-extrabold text-slate-900">
                    Panduan &amp; Arahan Pembayaran Pada Invois
                  </label>
                  <textarea
                    rows={2}
                    value={paymentSettings.paymentInstructions || ''}
                    onChange={(e) => setPaymentSettings(prev => ({ ...prev, paymentInstructions: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-asmm-red"
                    placeholder="Sila masukkan nombor invois atau tujuan bayaran pada rujukan perbankan internet anda."
                  />
                </div>

                {/* QR Toggle Switch */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
                  <div className="space-y-0.5 pr-4">
                    <span className="text-xs font-extrabold text-slate-900">
                      Aktifkan Kaedah DuitNow QR &amp; Pindahan Manual
                    </span>
                    <p className="text-[11px] text-slate-500">
                      Memaparkan kod QR dan akaun bank rasmi persatuan pada halaman pembayaran invois &amp; sumbangan.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPaymentSettings(prev => ({ ...prev, qrEnabled: !prev.qrEnabled }))}
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${paymentSettings.qrEnabled ? 'bg-asmm-red' : 'bg-slate-300'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${paymentSettings.qrEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              </div>

              {/* Card 2: ToyyibPay FPX Gateway Configuration */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-6">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="p-2.5 bg-amber-50 text-amber-600 rounded-2xl border border-amber-100">
                    <CreditCard className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900">
                      Konfigurasi Gateway ToyyibPay FPX (Akaun Induk)
                    </h3>
                    <p className="text-[11px] text-slate-500 font-medium">
                      Gerbang pembayaran dalam talian automatik FPX berdaftar bawah kelulusan ROS Persatuan.
                    </p>
                  </div>
                </div>

                {/* FPX Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
                  <div className="space-y-0.5 pr-4">
                    <span className="text-xs font-extrabold text-slate-900">
                      Aktifkan Gerbang FPX ToyyibPay
                    </span>
                    <p className="text-[11px] text-slate-500">
                      Membolehkan pembayar membuat bayaran terus melalui perbankan internet rasmi (Maybank2u, CIMB, BIMB dsb).
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPaymentSettings(prev => ({ ...prev, fpxEnabled: !prev.fpxEnabled }))}
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${paymentSettings.fpxEnabled ? 'bg-amber-500' : 'bg-slate-300'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${paymentSettings.fpxEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Secret Key */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold text-slate-900">
                      User Secret Key
                    </label>
                    <input
                      type="text"
                      value={paymentSettings.toyyibPaySecretKey}
                      onChange={(e) => setPaymentSettings(prev => ({ ...prev, toyyibPaySecretKey: e.target.value }))}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-asmm-red"
                      placeholder="w3gu2gf4-o2vg-lp95-jbic-aor35d2w8c2t"
                    />
                  </div>

                  {/* Category Code */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold text-slate-900">
                      Category Code
                    </label>
                    <input
                      type="text"
                      value={paymentSettings.toyyibPayCategoryCode}
                      onChange={(e) => setPaymentSettings(prev => ({ ...prev, toyyibPayCategoryCode: e.target.value }))}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-asmm-red"
                      placeholder="htnqvub2"
                    />
                  </div>
                </div>

                {/* Base URL Radio / Select */}
                <div className="space-y-1.5">
                  <label className="text-xs font-extrabold text-slate-900">
                    Mod Gateway (Environment):
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setPaymentSettings(prev => ({ ...prev, toyyibPayBaseUrl: 'https://dev.toyyibpay.com' }))}
                      className={`p-3 rounded-2xl border text-left cursor-pointer transition-all ${paymentSettings.toyyibPayBaseUrl.includes('dev')
                        ? 'bg-amber-50 border-amber-400 text-amber-950 font-black'
                        : 'bg-white border-slate-200 text-slate-600'
                        }`}
                    >
                      <p className="text-xs">Sandbox / Testing</p>
                      <p className="text-[10px] text-slate-400 font-mono mt-0.5">https://dev.toyyibpay.com</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentSettings(prev => ({ ...prev, toyyibPayBaseUrl: 'https://toyyibpay.com' }))}
                      className={`p-3 rounded-2xl border text-left cursor-pointer transition-all ${!paymentSettings.toyyibPayBaseUrl.includes('dev')
                        ? 'bg-emerald-50 border-emerald-400 text-emerald-950 font-black'
                        : 'bg-white border-slate-200 text-slate-600'
                        }`}
                    >
                      <p className="text-xs">Live Production</p>
                      <p className="text-[10px] text-slate-400 font-mono mt-0.5">https://toyyibpay.com</p>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Visual Bank Card Mockup & Summary (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              {/* Card 3: Visual Bank Card Mockup */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <span className="text-xs font-black text-slate-900 uppercase tracking-wider">
                    Kad Perbankan Rasmi Persatuan
                  </span>
                  <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200">
                    Akaun Induk
                  </span>
                </div>

                {/* ATM / Bank Card Visual Component */}
                <div className="w-full rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-red-950 p-5 text-white shadow-xl relative overflow-hidden border border-slate-800 space-y-4">
                  {/* Decorative Elements */}
                  <div className="absolute -right-8 -bottom-8 w-36 h-36 bg-amber-500/10 rounded-full blur-2xl pointer-events-none"></div>
                  <div className="absolute right-4 top-4 opacity-15">
                    <img src="/logo-pssasmm.png" alt="PSSASMM" className="w-20 h-20 object-contain" />
                  </div>

                  {/* Top Row: Bank Name & Chip */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-mono tracking-wider text-amber-400 uppercase">Akaun Rasmi Pertubuhan</p>
                      <p className="text-sm font-black text-white">{paymentSettings.bankName}</p>
                    </div>
                    {/* Simulated EMV Chip */}
                    <div className="w-10 h-7 rounded-md bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 border border-amber-200 shadow-xs flex items-center justify-center">
                      <div className="w-7 h-5 border border-amber-800/40 rounded-xs"></div>
                    </div>
                  </div>

                  {/* Account Number Display */}
                  <div className="pt-2">
                    <p className="text-[10px] font-medium text-slate-400">Nombor Akaun PSSASMM</p>
                    <p className="text-lg sm:text-xl font-mono font-black tracking-widest text-amber-100 mt-0.5">
                      {paymentSettings.accountNo || '02011010087654'}
                    </p>
                  </div>

                  {/* Bottom Row: Account Holder & DuitNow Badge */}
                  <div className="pt-2 border-t border-white/10 flex items-end justify-between gap-2">
                    <div className="space-y-0.5">
                      <p className="text-[9px] uppercase tracking-wider text-slate-400">Nama Pemegang Akaun</p>
                      <p className="text-xs font-black text-white line-clamp-1">
                        {paymentSettings.accountHolder}
                      </p>
                    </div>
                    <span className="text-[9px] font-bold px-2 py-0.5 bg-red-600/80 text-white rounded-md shrink-0">
                      DuitNow
                    </span>
                  </div>
                </div>

                {/* QR Code Preview Box (Digitalized SVG 1:1) */}
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 text-center space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-black text-slate-900 flex items-center gap-1.5">
                      <QrCode className="w-3.5 h-3.5 text-amber-600" />
                      <span>Kod DuitNow QR Rasmi (Vektor SVG 1:1)</span>
                    </p>
                    {qrSvgString ? (
                      <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        Digital SVG 1:1
                      </span>
                    ) : paymentSettings.qrImageUrl ? (
                      <span className="text-[10px] font-bold text-slate-600 bg-slate-200/80 px-2 py-0.5 rounded-full">
                        Imej Raster
                      </span>
                    ) : null}
                  </div>

                  {/* 1:1 Aspect Ratio Display Box Matching Invoices */}
                  <div className="w-48 h-48 sm:w-56 sm:h-56 mx-auto bg-white p-3.5 rounded-3xl border-2 border-slate-900/10 shadow-sm relative flex items-center justify-center">
                    {isProcessingQrSvg && (
                      <div className="absolute inset-0 bg-white/95 backdrop-blur-xs flex flex-col items-center justify-center rounded-2xl z-10 space-y-2 text-center p-3">
                        <Loader2 className="w-7 h-7 text-amber-500 animate-spin" />
                        <span className="text-xs font-black text-slate-800">Menjana Digital SVG 1:1...</span>
                        <span className="text-[10px] text-slate-500">Mengekstrak kod DuitNow QR</span>
                      </div>
                    )}

                    {qrSvgString ? (
                      <div
                        className="w-full h-full aspect-square flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:aspect-square [&>svg]:block"
                        dangerouslySetInnerHTML={{ __html: qrSvgString }}
                      />
                    ) : paymentSettings.qrImageUrl ? (
                      <img
                        src={paymentSettings.qrImageUrl}
                        alt="QR Persatuan"
                        className="w-full h-full aspect-square object-contain mx-auto rounded-xl"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/logo-pssasmm.png';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full rounded-2xl border-2 border-dashed border-slate-200 bg-white flex flex-col items-center justify-center text-slate-400 p-3">
                        <QrCode className="w-10 h-10 mb-1 opacity-60 text-slate-400" />
                        <span className="text-[11px] font-black uppercase tracking-wider text-slate-600">DuitNow QR</span>
                        <span className="text-[10px] text-slate-400 mt-1">Tiada Imej QR</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons for QR */}
                  <div className="flex items-center justify-center gap-2 pt-1">
                    {(qrSvgString || paymentSettings.qrImageUrl) && (
                      <button
                        type="button"
                        onClick={handleDownloadPersatuanQr}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all shadow-sm cursor-pointer"
                        title="Muat turun fail SVG kod QR persatuan"
                      >
                        <Download className="w-3.5 h-3.5 text-amber-400" />
                        <span>Muat Turun SVG</span>
                      </button>
                    )}
                    {paymentSettings.qrImageUrl && !qrSvgString && (
                      <button
                        type="button"
                        onClick={() => handleDigitizeExistingUrl(paymentSettings.qrImageUrl || '')}
                        disabled={isProcessingQrSvg}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-bold rounded-xl transition-all border border-amber-200 cursor-pointer disabled:opacity-50"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                        <span>Tukar ke SVG 1:1</span>
                      </button>
                    )}
                  </div>

                  <p className="text-[10px] text-slate-500 font-medium max-w-xs mx-auto">
                    Imbas kod QR digital ini untuk pembayaran terus ke akaun rasmi induk PSSASMM.
                  </p>
                </div>
              </div>

              {/* Card 4: Aliran Dana Ringkasan */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-3">
                <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <Building className="w-4 h-4 text-blue-600" />
                  <h4 className="text-xs font-black text-slate-900 uppercase tracking-wide">
                    Ringkasan Aliran Dana PSSASMM
                  </h4>
                </div>

                <div className="space-y-2 text-xs text-slate-600">
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-asmm-red mt-1.5 shrink-0"></span>
                    <p><strong className="text-slate-900">Yuran Keahlian &amp; Pendaftaran:</strong> Masuk ke akaun induk persatuan untuk audit ROS.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
                    <p><strong className="text-slate-900">Infaq 'Semua Gelanggang':</strong> Disalurkan terus ke akaun induk bagi diagihkan sama rata atau projek am.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 shrink-0"></span>
                    <p><strong className="text-slate-900">Vendor / Pihak Ketiga (PSSASMM):</strong> Diterima langsung oleh bendahari persatuan.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-slate-400 mt-1.5 shrink-0"></span>
                    <p><strong className="text-slate-900">Gelanggang Khusus:</strong> Gelanggang individu (Pusaka Warisan, dsb.) boleh mengurus QR gelanggang masing-masing jika dibenarkan.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* TAB 1: BENGKUNG */}
      {activeMainTab === 'bengkung' && (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-4 sm:p-6 space-y-6">
          {/* Section Header with Module Filter */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-50 text-asmm-red rounded-2xl border border-red-200/80 shadow-2xs shrink-0">
                <ListOrdered className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm sm:text-base font-extrabold text-slate-900">
                    Susunan &amp; Gelaran Rasmi Bengkung ASMM
                  </h3>
                  <span className="text-[10px] font-black bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded-full border border-slate-200">
                    {bengkungList.length} Peringkat Terdaftar
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                  Tetapkan gelaran rasmi silat (*Rank Title*), serta susun urutan hierarki kenaikan bengkung perguruan.
                </p>
              </div>
            </div>

            {/* Filter by Module Chips & Add Bengkung Button */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 lg:pb-0">
                {['all', 'Hitam', 'Putih', 'Merah', 'Kuning', 'Biru'].map((mod) => (
                  <button
                    key={mod}
                    type="button"
                    onClick={() => setBengkungFilterModule(mod)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 ${bengkungFilterModule === mod
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                      }`}
                  >
                    {mod === 'all' ? 'Semua Modul' : mod}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={handleOpenAddModal}
                className="px-3.5 py-1.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer shrink-0 active:scale-95 ml-auto sm:ml-0"
                title="Tambah pangkat bengkung baharu"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Tambah Bengkung</span>
              </button>
            </div>
          </div>

          {/* List of Bengkung Items */}
          <div className="space-y-3">
            {filteredBengkungList.map((rank) => {
              const globalIndex = bengkungList.findIndex(r => r.code === rank.code);
              const isFirst = globalIndex === 0;
              const isLast = globalIndex === bengkungList.length - 1;

              return (
                <div
                  key={rank.code}
                  className="p-3.5 sm:p-4 rounded-2xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-2xs transition-all flex flex-col md:flex-row md:items-center justify-between gap-3.5"
                >
                  {/* Left Controls: Editable Position Number, Arrows & Belt Ribbon */}
                  <div className="flex items-center gap-2.5 shrink-0 flex-wrap sm:flex-nowrap">
                    {/* Editable Position Number (Click to edit and reorganize) */}
                    <OrderNumberInput
                      currentPosition={globalIndex + 1}
                      maxPosition={bengkungList.length}
                      onCommitPosition={(newPos) => handleMoveToPosition(globalIndex, newPos)}
                    />

                    {/* Up & Down Buttons */}
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleMoveBengkung(globalIndex, 'up')}
                        disabled={isFirst}
                        className="w-7 h-5 bg-slate-100 hover:bg-slate-200 disabled:opacity-30 disabled:hover:bg-slate-100 rounded-md flex items-center justify-center text-slate-700 cursor-pointer disabled:cursor-not-allowed transition-all"
                        title="Naikkan kedudukan (Up)"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveBengkung(globalIndex, 'down')}
                        disabled={isLast}
                        className="w-7 h-5 bg-slate-100 hover:bg-slate-200 disabled:opacity-30 disabled:hover:bg-slate-100 rounded-md flex items-center justify-center text-slate-700 cursor-pointer disabled:cursor-not-allowed transition-all"
                        title="Turunkan kedudukan (Down)"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Visual Belt Badge with ample width so words are never obstructed by cula lines */}
                    <div className="w-44 sm:w-52 md:w-56 hidden sm:block shrink-0 rounded-lg overflow-hidden shadow-2xs">
                      <BengkungCulaBadge bengkung={rank.code} size="sm" fullWidth={true} />
                    </div>
                  </div>

                  {/* Middle Form: Editable Rank Title (Shorter width on desktop) */}
                  <div className="flex-1 sm:max-w-xs md:max-w-sm min-w-0">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1 truncate">
                      Gelaran Silat
                    </label>
                    <input
                      type="text"
                      value={rank.rankTitle}
                      onChange={(e) => handleUpdateBengkungField(globalIndex, 'rankTitle', e.target.value)}
                      className="w-full px-3 py-1.5 bg-slate-50/80 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all"
                      placeholder="Gelaran silat..."
                    />
                  </div>

                  {/* Right Tags & Actions */}
                  <div className="flex items-center gap-1.5 shrink-0 flex-wrap pt-1 md:pt-0">
                    <span className="text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-2 py-1 rounded-lg">
                      {rank.culaCount === 0 ? 'Tanpa Cula' : `${rank.culaCount} Cula`}
                    </span>
                    <span className="text-[10px] font-mono text-slate-500 px-1">
                      {rank.code}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleOpenEditModal(rank)}
                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl border border-transparent hover:border-blue-200 transition-all cursor-pointer shrink-0"
                      title={`Kemaskini maklumat pangkat ${rank.code}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOpenDeleteModal(rank)}
                      className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl border border-transparent hover:border-rose-200 transition-all cursor-pointer shrink-0"
                      title={`Padam pangkat ${rank.code} daripada hierarki`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer Bar for Bengkung List */}
          <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <span className="text-slate-400 font-medium text-[11px]">
              * Perubahan susunan dan gelaran silat akan memberi kesan kepada urutan pendaftaran, silibus, direktori ahli, dan sistem pensijilan.
            </span>

            <div className="flex items-center gap-2 self-end sm:self-center">
              <button
                type="button"
                onClick={handleResetBengkungList}
                disabled={isSavingBengkung}
                className="px-4 py-2 text-slate-600 hover:text-slate-900 font-bold transition-colors cursor-pointer"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleSaveBengkungList}
                disabled={isSavingBengkung}
                className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                <Save className="w-4 h-4" />
                <span>{isSavingBengkung ? 'Menyimpan...' : 'Simpan'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: YURAN UJIAN BENGKUNG */}
      {activeMainTab === 'yuran' && (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-4 sm:p-6 space-y-6">
          {/* Header with Info */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-50 text-amber-600 rounded-2xl border border-amber-200/80 shadow-2xs shrink-0">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm sm:text-base font-extrabold text-slate-900">
                    Yuran Ujian Kenaikan Bengkung
                  </h3>
                  <span className="text-[10px] font-black bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full border border-amber-200">
                    Kadar Mengikut Modul Sahaja
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                  Kadar yuran ujian ditetapkan secara seragam mengikut setiap modul utama perguruan. Semua peringkat cula di dalam modul yang sama akan dikenakan caj ini secara automatik.
                </p>
              </div>
            </div>
          </div>

          {/* Module-Only Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                key: 'hitam' as const,
                label: 'Modul Bengkung Hitam',
                sub: 'Tunas Pendekar (Kanak-kanak & Awal)',
                color: 'bg-slate-900 text-white',
                ranks: bengkungList.filter(r => r.code.toLowerCase().includes('hitam'))
              },
              {
                key: 'putih' as const,
                label: 'Modul Bengkung Putih',
                sub: 'Pesilat Dewasa & Remaja',
                color: 'bg-white text-slate-800 border border-slate-300',
                ranks: bengkungList.filter(r => r.code.toLowerCase().includes('putih'))
              },
              {
                key: 'merah' as const,
                label: 'Modul Bengkung Merah',
                sub: 'Pembantu Jurulatih Gelanggang',
                color: 'bg-red-600 text-white',
                ranks: bengkungList.filter(r => r.code.toLowerCase().includes('merah'))
              },
              {
                key: 'kuning' as const,
                label: 'Modul Bengkung Kuning',
                sub: 'Jurulatih Bertauliah',
                color: 'bg-yellow-400 text-slate-950 font-black',
                ranks: bengkungList.filter(r => r.code.toLowerCase().includes('kuning') && !r.code.toLowerCase().includes('guru'))
              },
              {
                key: 'biru' as const,
                label: 'Modul Bengkung Biru',
                sub: 'Pendekar Pertubuhan',
                color: 'bg-blue-600 text-white',
                ranks: bengkungList.filter(r => r.code.toLowerCase().includes('biru'))
              }
            ].map((mod) => (
              <div
                key={mod.key}
                className="p-4 sm:p-5 rounded-2xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:border-slate-300 hover:shadow-xs transition-all space-y-3.5 flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2.5 py-1 rounded-xl font-extrabold shadow-2xs ${mod.color}`}>
                      {mod.label}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                    {mod.sub}
                  </p>
                </div>

                {/* Price Input & Quick Adjusters */}
                <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-2xs space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-slate-700">Kadar Seragam:</span>
                    <div className="relative w-28">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">
                        RM
                      </span>
                      <input
                        type="number"
                        step="5"
                        min="0"
                        value={feeConfig[mod.key] ?? 0}
                        onChange={(e) => handleModulePriceChange(mod.key, parseFloat(e.target.value) || 0)}
                        className="w-full pl-8 pr-2.5 py-1.5 bg-slate-50 focus:bg-white border border-slate-300 rounded-xl text-xs font-black font-mono text-slate-900 focus:outline-none focus:border-asmm-red text-right shadow-2xs transition-all"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-1 pt-1.5 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => handleQuickAdjustModule(mod.key, -5)}
                      className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[10px] rounded-lg border border-slate-200/80 transition-colors shadow-2xs cursor-pointer active:scale-95"
                      title="Tolak RM 5"
                    >
                      -5
                    </button>
                    <button
                      type="button"
                      onClick={() => handleQuickAdjustModule(mod.key, 5)}
                      className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[10px] rounded-lg border border-slate-200/80 transition-colors shadow-2xs cursor-pointer active:scale-95"
                      title="Tambah RM 5"
                    >
                      +5
                    </button>
                    <button
                      type="button"
                      onClick={() => handleQuickAdjustModule(mod.key, 10)}
                      className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[10px] rounded-lg border border-slate-200/80 transition-colors shadow-2xs cursor-pointer active:scale-95"
                      title="Tambah RM 10"
                    >
                      +10
                    </button>
                  </div>
                </div>

                {/* Bengkung items under this module */}
                <div className="pt-2 border-t border-slate-200/60 space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">
                    Cula Termasuk (Semua RM {(feeConfig[mod.key] ?? 0).toFixed(2)}):
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {mod.ranks.map(r => (
                      <span
                        key={r.code}
                        className="text-[10px] font-semibold bg-white text-slate-700 px-2 py-0.5 rounded border border-slate-200 shadow-2xs"
                        title={r.rankTitle}
                      >
                        {r.code}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Actions for Fees */}
          <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <span className="text-slate-400 font-medium text-[11px]">
              * Perubahan yuran akan disimpan terus ke pangkalan data dan diaplikasikan serta-merta pada sistem invois.
            </span>

            <div className="flex items-center gap-2 self-end sm:self-center">
              <button
                type="button"
                onClick={handleResetFeesToDefault}
                disabled={isSaving}
                className="px-4 py-2 text-slate-600 hover:text-slate-900 font-bold transition-colors cursor-pointer"
              >
                Batal &amp; Set Semula
              </button>
              <button
                type="button"
                onClick={handleSaveFees}
                disabled={isSaving}
                className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                <Check className="w-4 h-4" />
                <span>{isSaving ? 'Menyimpan...' : 'Simpan Tetapan Yuran'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* MODAL TAMBAH PANGKAT BENGKUNG BAHARU */}
      {isAddModalOpen && (
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => !isSubmittingAdd && setIsAddModalOpen(false)}
          title="Tambah Pangkat Bengkung Baharu"
          maxWidth="max-w-xl"
        >
          <form onSubmit={handleConfirmAddRank} className="p-5 sm:p-6 space-y-4">
            {/* Live Ribbon Preview */}
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
              <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                <span>Pratonton Tali Pinggang &amp; Cula</span>
                <span className="text-asmm-red font-mono text-xs">{newCode || 'Peringkat Baharu'}</span>
              </div>
              <div className="rounded-xl overflow-hidden shadow-2xs border border-slate-200">
                {(() => {
                  const selPreset = BELT_COLOR_PRESETS.find(p => p.id === newPresetId) || BELT_COLOR_PRESETS[3];
                  const numCula = Math.max(0, Math.min(12, Number(newCulaCount) || 0));
                  const isCompact = numCula >= 4;
                  const culaWidthClass = isCompact ? 'w-1.5' : 'w-2';
                  const culaGapClass = isCompact ? 'gap-1' : 'gap-1.5';

                  return (
                    <div className={`h-9 font-bold flex items-center justify-between relative overflow-hidden px-4 ${selPreset.beltBgClass} ${selPreset.textColorClass}`}>
                      <span className="tracking-wide font-black z-10 select-none whitespace-nowrap text-xs sm:text-sm">
                        {newCode.trim() || 'Nama Bengkung'}
                      </span>
                      {numCula > 0 && (
                        <div className={`flex items-center ${culaGapClass} h-full absolute right-4 top-0 bottom-0 z-10`}>
                          {Array.from({ length: numCula }).map((_, i) => (
                            <span key={i} className={`block ${culaWidthClass} h-full ${selPreset.culaBgClass}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-500 font-medium">
                <span>Gelaran Silat: <strong>{newRankTitle.trim() || 'Pesilat'}</strong></span>
                <span>{Number(newCulaCount) === 0 ? 'Tanpa Cula' : `${newCulaCount} Cula`}</span>
              </div>
            </div>

            {/* Form Fields Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {/* Kod / Pangkat Bengkung */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Kod / Pangkat Bengkung <span className="text-asmm-red">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newCode}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewCode(val);
                    if (!newBengkungName || newBengkungName.startsWith('Bengkung ')) {
                      setNewBengkungName(`Bengkung ${val}`);
                    }
                  }}
                  className="w-full px-3 py-2 bg-slate-50 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all"
                  placeholder="Contoh: Kuning 8, Merah 4"
                />
                <span className="text-[10px] text-slate-400 mt-0.5 block">Nama ringkas pengenalan bengkung.</span>
              </div>

              {/* Gelaran Silat (Rank Title) */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Gelaran Silat (*Rank Title*) <span className="text-asmm-red">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newRankTitle}
                  onChange={(e) => setNewRankTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all"
                  placeholder="Contoh: Jurulatih Kanan, Pendekar Muda"
                />
                <span className="text-[10px] text-slate-400 mt-0.5 block">Gelaran sandang rasmi bagi pesilat.</span>
              </div>

              {/* Modul Bengkung */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Kategori Modul Bengkung
                </label>
                <select
                  value={newModule}
                  onChange={(e) => handleModuleSelect(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all cursor-pointer"
                >
                  <option value="Modul Bengkung Hitam">Modul Bengkung Hitam</option>
                  <option value="Modul Bengkung Putih">Modul Bengkung Putih</option>
                  <option value="Modul Bengkung Merah">Modul Bengkung Merah</option>
                  <option value="Modul Bengkung Kuning">Modul Bengkung Kuning</option>
                  <option value="Modul Bengkung Biru">Modul Bengkung Biru</option>
                  <option value="Modul Bengkung Hijau">Modul Bengkung Hijau</option>
                  <option value="Kepimpinan Perguruan">Kepimpinan Perguruan</option>
                  <option value="Pangkat Khas">Pangkat Khas / Lain-lain</option>
                </select>
              </div>

              {/* Bilangan Cula */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Bilangan Cula (Jalur)
                </label>
                <input
                  type="number"
                  min={0}
                  max={12}
                  value={newCulaCount}
                  onChange={(e) => setNewCulaCount(parseInt(e.target.value, 10) || 0)}
                  className="w-full px-3 py-2 bg-slate-50 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all"
                />
                <span className="text-[10px] text-slate-400 mt-0.5 block">0 untuk tanpa cula, atau 1 hingga 12 jalur.</span>
              </div>
            </div>

            {/* Pilihan Warna Tali Pinggang (Belt Visual Presets) */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Warna &amp; Corak Tali Pinggang
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {BELT_COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setNewPresetId(preset.id)}
                    className={`p-2 rounded-xl border text-left transition-all flex items-center gap-2 cursor-pointer ${newPresetId === preset.id
                        ? 'border-asmm-red bg-red-50/60 ring-2 ring-red-100 shadow-2xs'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                  >
                    <span
                      className="w-4 h-4 rounded-full shrink-0 border border-slate-300 shadow-2xs"
                      style={{ backgroundColor: preset.sampleColor }}
                    />
                    <span className="text-[11px] font-bold text-slate-800 truncate">
                      {preset.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Nama Lengkap Bengkung */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Nama Penuh Bengkung (Untuk Sijil / Dokumen)
              </label>
              <input
                type="text"
                value={newBengkungName}
                onChange={(e) => setNewBengkungName(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all"
                placeholder="Contoh: Bengkung Kuning (Darjah 8)"
              />
            </div>

            {/* Kedudukan Hierarki */}
            <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200 space-y-2">
              <label className="block text-xs font-bold text-slate-700">
                Kedudukan dalam Hierarki Kenaikan Pangkat
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setNewInsertPos('end')}
                  className={`py-2 px-3 rounded-xl border text-xs font-bold transition-all text-center cursor-pointer ${newInsertPos === 'end'
                      ? 'bg-slate-900 text-white border-slate-900 shadow-2xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                >
                  Di Akhir (Pangkat Tinggi)
                </button>
                <button
                  type="button"
                  onClick={() => setNewInsertPos('start')}
                  className={`py-2 px-3 rounded-xl border text-xs font-bold transition-all text-center cursor-pointer ${newInsertPos === 'start'
                      ? 'bg-slate-900 text-white border-slate-900 shadow-2xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                >
                  Di Awal (Pangkat Asas)
                </button>
                <button
                  type="button"
                  onClick={() => setNewInsertPos('after')}
                  className={`py-2 px-3 rounded-xl border text-xs font-bold transition-all text-center cursor-pointer ${newInsertPos === 'after'
                      ? 'bg-slate-900 text-white border-slate-900 shadow-2xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                >
                  Selepas Bengkung...
                </button>
              </div>

              {newInsertPos === 'after' && (
                <div className="pt-1">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                    Pilih Bengkung Terdahulu:
                  </label>
                  <select
                    value={newAfterCode}
                    onChange={(e) => setNewAfterCode(e.target.value)}
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none cursor-pointer"
                  >
                    {bengkungList.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.code} - {r.rankTitle}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                disabled={isSubmittingAdd}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={isSubmittingAdd}
                className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                <Plus className="w-4 h-4" />
                <span>{isSubmittingAdd ? 'Menyimpan...' : 'Tambah Pangkat Sekarang'}</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* MODAL KEMASKINI PANGKAT BENGKUNG */}
      {isEditModalOpen && rankToEdit && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => !isSubmittingEdit && setIsEditModalOpen(false)}
          title="Kemaskini Maklumat Pangkat Bengkung"
          maxWidth="max-w-xl"
        >
          <form onSubmit={handleConfirmEditRank} className="p-5 sm:p-6 space-y-4">
            {/* Live Ribbon Preview */}
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
              <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                <span>Pratonton Tali Pinggang &amp; Cula</span>
                <span className="text-asmm-red font-mono text-xs">{editCode || 'Bengkung'}</span>
              </div>
              <div className="rounded-xl overflow-hidden shadow-2xs border border-slate-200">
                {(() => {
                  const selPreset = BELT_COLOR_PRESETS.find(p => p.id === editPresetId) || BELT_COLOR_PRESETS[3];
                  const numCula = Math.max(0, Math.min(12, Number(editCulaCount) || 0));
                  const isCompact = numCula >= 4;
                  const culaWidthClass = isCompact ? 'w-1.5' : 'w-2';
                  const culaGapClass = isCompact ? 'gap-1' : 'gap-1.5';

                  return (
                    <div className={`h-9 font-bold flex items-center justify-between relative overflow-hidden px-4 ${selPreset.beltBgClass} ${selPreset.textColorClass}`}>
                      <span className="tracking-wide font-black z-10 select-none whitespace-nowrap text-xs sm:text-sm">
                        {editCode.trim() || 'Nama Bengkung'}
                      </span>
                      {numCula > 0 && (
                        <div className={`flex items-center ${culaGapClass} h-full absolute right-4 top-0 bottom-0 z-10`}>
                          {Array.from({ length: numCula }).map((_, i) => (
                            <span key={i} className={`block ${culaWidthClass} h-full ${selPreset.culaBgClass}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-500 font-medium">
                <span>Gelaran Silat: <strong>{editRankTitle.trim() || 'Pesilat'}</strong></span>
                <span>{Number(editCulaCount) === 0 ? 'Tanpa Cula' : `${editCulaCount} Cula`}</span>
              </div>
            </div>

            {/* Form Fields Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {/* Kod / Pangkat Bengkung */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Kod / Pangkat Bengkung <span className="text-asmm-red">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editCode}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditCode(val);
                    if (!editBengkungName || editBengkungName.startsWith('Bengkung ')) {
                      setEditBengkungName(`Bengkung ${val}`);
                    }
                  }}
                  className="w-full px-3 py-2 bg-slate-50 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all"
                  placeholder="Contoh: Kuning 8, Merah 4"
                />
                <span className="text-[10px] text-slate-400 mt-0.5 block">Nama ringkas pengenalan bengkung.</span>
              </div>

              {/* Gelaran Silat (Rank Title) */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Gelaran Silat (*Rank Title*) <span className="text-asmm-red">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editRankTitle}
                  onChange={(e) => setEditRankTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all"
                  placeholder="Contoh: Jurulatih Kanan, Pendekar Muda"
                />
                <span className="text-[10px] text-slate-400 mt-0.5 block">Gelaran sandang rasmi bagi pesilat.</span>
              </div>

              {/* Modul Bengkung */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Kategori Modul Bengkung
                </label>
                <select
                  value={editModule}
                  onChange={(e) => handleEditModuleSelect(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all cursor-pointer"
                >
                  <option value="Modul Bengkung Hitam">Modul Bengkung Hitam</option>
                  <option value="Modul Bengkung Putih">Modul Bengkung Putih</option>
                  <option value="Modul Bengkung Merah">Modul Bengkung Merah</option>
                  <option value="Modul Bengkung Kuning">Modul Bengkung Kuning</option>
                  <option value="Modul Bengkung Biru">Modul Bengkung Biru</option>
                  <option value="Modul Bengkung Hijau">Modul Bengkung Hijau</option>
                  <option value="Kepimpinan Perguruan">Kepimpinan Perguruan</option>
                  <option value="Pangkat Khas">Pangkat Khas / Lain-lain</option>
                </select>
              </div>

              {/* Bilangan Cula */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Bilangan Cula (Jalur)
                </label>
                <input
                  type="number"
                  min={0}
                  max={12}
                  value={editCulaCount}
                  onChange={(e) => setEditCulaCount(parseInt(e.target.value, 10) || 0)}
                  className="w-full px-3 py-2 bg-slate-50 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all"
                />
                <span className="text-[10px] text-slate-400 mt-0.5 block">0 untuk tanpa cula, atau 1 hingga 12 jalur.</span>
              </div>
            </div>

            {/* Pilihan Warna Tali Pinggang (Belt Visual Presets) */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Warna &amp; Corak Tali Pinggang
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {BELT_COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setEditPresetId(preset.id)}
                    className={`p-2 rounded-xl border text-left transition-all flex items-center gap-2 cursor-pointer ${
                      editPresetId === preset.id
                        ? 'border-asmm-red bg-red-50/60 ring-2 ring-red-100 shadow-2xs'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <span
                      className="w-4 h-4 rounded-full shrink-0 border border-slate-300 shadow-2xs"
                      style={{ backgroundColor: preset.sampleColor }}
                    />
                    <span className="text-[11px] font-bold text-slate-800 truncate">
                      {preset.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Nama Lengkap Bengkung */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Nama Penuh Bengkung (Untuk Sijil / Dokumen)
              </label>
              <input
                type="text"
                value={editBengkungName}
                onChange={(e) => setEditBengkungName(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 focus:bg-white border border-slate-200 focus:border-asmm-red rounded-xl text-xs font-bold text-slate-900 focus:outline-none transition-all"
                placeholder="Contoh: Bengkung Kuning (Darjah 7)"
              />
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                disabled={isSubmittingEdit}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={isSubmittingEdit}
                className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                <Save className="w-4 h-4" />
                <span>{isSubmittingEdit ? 'Menyimpan...' : 'Simpan Kemaskini'}</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* MODAL PENGESAHAN PADAM PANGKAT BENGKUNG */}
      {isDeleteModalOpen && rankToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => !isSubmittingDelete && setIsDeleteModalOpen(false)}
          title="Padam Pangkat Bengkung"
          maxWidth="max-w-md"
        >
          <div className="p-5 space-y-4">
            <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-2xl flex items-start gap-3">
              <div className="p-2 bg-rose-100 text-rose-700 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-rose-900">Pengesahan Pemadaman Pangkat</h4>
                <p className="text-[11px] text-rose-700 leading-relaxed">
                  Adakah anda pasti mahu memadam pangkat <strong>{rankToDelete.code}</strong> ({rankToDelete.rankTitle}) daripada hierarki rasmi ASMM?
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200 space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400 font-bold">Kod Bengkung:</span>
                <span className="text-slate-900 font-black">{rankToDelete.code}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400 font-bold">Gelaran Silat:</span>
                <span className="text-slate-900 font-extrabold">{rankToDelete.rankTitle}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400 font-bold">Modul Bengkung:</span>
                <span className="text-slate-700 font-semibold">{rankToDelete.module}</span>
              </div>
              <div className="pt-2 border-t border-slate-200/60">
                <div className="w-full rounded-lg overflow-hidden shadow-2xs">
                  <BengkungCulaBadge bengkung={rankToDelete.code} size="sm" fullWidth={true} />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isSubmittingDelete}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteRank}
                disabled={isSubmittingDelete}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-extrabold text-xs rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isSubmittingDelete ? 'Memadam...' : 'Ya, Padam Pangkat'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
